import { canonicalPathFor } from "../lib/routeSlugs";
import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { sheetsTranslations } from "./sheets";
import { configuratorTranslations } from "./configurator";

export type Lang = "pl" | "en" | "fr" | "de" | "it";

export const translations: Record<Lang, Record<string, string>> = {
  pl: {
    // Nav
    "nav.configurator": "Kreator naklejek",
    "nav.quickPayment": "Szybka płatność",
    "nav.blog": "Blog",
    "nav.foils": "Folie i laminaty",
    "nav.faq": "Często zadawane pytania",
    "nav.contact": "Kontakt / O nas",
    "nav.about": "O nas",
    "nav.portfolio": "Portfolio",
    "nav.cart": "Koszyk",
    "nav.loggedIn": "Zalogowany",

    // Hero
    "hero.badge": "Naklejki Premium",
    "hero.followUs": "Kliknij aby nas zaobserwować →",
    "hero.description": "Naklejki premium w najwyższej jakości – holograficzne, brokatowe, przezroczyste, opalizowane i świecące w nocy oraz wiele innych. Drukarnia online. Skonfiguruj i zamów w minutę.",
    "hero.cta": "Stwórz naklejki",
    "welcome.title": "−10% rabatu powitalnego",
    "welcome.description": "Jednorazowy rabat dla nowych klientów na pierwsze zamówienie do 1000 zł",

    // Configurator
    "config.title": "Parametry",
    "config.titleHighlight": "naklejek",
    "config.shape": "Kształt",
    "config.foil": "Rodzaj folii - jest to podkład na którym drukowane są naklejki",
    "config.foilShort": "Rodzaj folii – jest to podkład na którym drukowane są naklejki",
    "config.laminate": "Rodzaj laminatu - jest to folia którą zostają pokryte naklejki",
    "config.laminateShort": "Rodzaj laminatu – jest to folia którą zostają pokryte naklejki",
    "config.dimensions": "Wymiary",
    "config.width": "Szerokość",
    "config.height": "Wysokość",
    "config.unit.mm": "milimetry",
    "config.unit.cm": "centymetry",
    "config.quantity": "Ilość",
    "config.file": "Plik projektu",
    "config.filePrompt": "Kliknij, aby wgrać pliki (PDF, PNG, TIFF, JPG, BMP)",
    "config.fileSelected": "Wybrano:",
    "config.fileOver10": "Pliki powyżej 20 MB prosimy wysyłać bezpośrednio na",
    "config.orVia": "lub przez",
    "config.calculate": "Oblicz Cenę",
    "config.summary": "Podsumowanie zamówienia",
    "config.price": "Cena:",
    "config.discount": "✓ Rabat ilościowy uwzględniony",
    "config.warning": "⚠ Prosimy o przemyślane zakupy.",
    "config.warningText": "Jeśli nie masz pewności, jak będą wyglądać naklejki — zwłaszcza te drukowane na folii przezroczystej lub naklejki bez poddruku białego na foliach holograficznych i metalicznych — zapoznaj się z sekcją",
    "config.warningFaq": "najczęściej zadawanych pytań",
    "config.warningBlogPrefix": "wpisami na naszym",
    "config.warningBlog": "blogu",
    "config.warningGhost": "zapytaj duszka",
    "config.warningOr": "lub skontaktuj się z nami z konkretnym pytaniem.",
    "config.warningText2": "Naklejki zamówione w kreatorze są drukowane dokładnie tak, jak zostały przez Ciebie zaprojektowane na wybranych foliach. Jeżeli po otrzymaniu przesyłki stwierdzisz, że wygląd naklejek Ci nie odpowiada, a wynika to z braku wcześniejszego zapoznania się z informacjami o produkcie, reklamacje nie będą uwzględniane.",
    
    "config.addToCart": "Do koszyka",
    "config.uploaded": "przesłano...",
    "config.remove": "usuń",
    "config.pcs": "szt.",
    "config.pieces": "sztuk",
    "config.shipDate": "Wysyłka do",
    "config.files": "Pliki",
    "config.minOrder": "Min. zamówienie dla tej folii:",
    "config.minOrderMissing": "brakuje",
    "config.whitePrintLabel": "Z poddrukiem w kolorze białym",
    "config.whitePrintSuffix": " – z poddrukiem w kolorze białym",
    "config.whitePrintDropdown": " · ⬤ druk w kolorze białym",

    // Gallery
    "gallery.title": "Nasze",
    "gallery.titleHighlight": "realizacje",
    "gallery.description": "Zobacz naklejki, które stworzyliśmy dla naszych klientów",
    "gallery.preparing": "Galeria w przygotowaniu",
    "gallery.soon": "Wkrótce dodamy zdjęcia i filmy z realizacji",

    // FAQ
    "faq.title": "Często zadawane",
    "faq.titleHighlight": "pytania",
    "faq.showAll": "Zobacz wszystkie pytania",

    // Contact
    "contact.title": "Napisz do",
    "contact.titleHighlight": "Nas",
    "contact.description": "O naklejkach wiemy wszystko! Napisz do nas, a odpowiemy na wszystkie Twoje pytania.",
    "contact.name": "Imię",
    "contact.surname": "Nazwisko",
    "contact.email": "Email",
    "contact.phone": "Numer telefonu",
    "contact.message": "Wiadomość",
    "contact.send": "Wyślij",
    "contact.success": "Wiadomość wysłana! Odpowiemy najszybciej jak to możliwe.",

    // Footer
    "footer.contact": "Kontakt",
    "footer.info": "Informacje",
    "footer.stickersCut": "Stickers Cut – nasz darmowy generator konturów cięcia",
    "footer.faq": "FAQ",
    "footer.terms": "Regulamin",
    "footer.privacy": "Polityka prywatności",
    "footer.cookies": "Polityka cookies",
    "footer.withdrawal": "Prawo odstąpienia",
    "footer.rights": "Wszelkie prawa zastrzeżone.",

    // Cookie banner
    "cookie.message": "Korzystamy z plików cookies wyłącznie w celach technicznych (sesja, koszyk, preferencje). Nie stosujemy cookies marketingowych ani analitycznych.",
    "cookie.learnMore": "Dowiedz się więcej o polityce cookies",
    "cookie.accept": "Rozumiem",

    // Blog
    "blog.title": "Poradnik",
    "blog.titleHighlight": "naklejek",
    "blog.description": "Eksperckie artykuły o druku naklejek, foliach, laminatach i przygotowaniu plików",
    "blog.readMore": "Czytaj więcej",
    "blog.comingSoon": "Artykuły pojawią się wkrótce",
    "blog.allArticles": "Wszystkie artykuły",
    "blog.notFound": "Artykuł nie znaleziony",
    "blog.backToBlog": "← Wróć do bloga",

    // Quick Payment
    "qp.title": "Szybka",
    "qp.titleHighlight": "płatność",
    "qp.description": "Wybierz metodę płatności i zapłać w kilka sekund",
    "qp.info": "Płatności obsługiwane przez Przelewy24. Wszystkie transakcje są szyfrowane i bezpieczne.",
    "qp.blik": "BLIK",
    "qp.blikDesc": "Szybka płatność kodem BLIK",
    "qp.transfer": "Przelewy24",
    "qp.transferDesc": "Przelew online przez Przelewy24",
    "qp.card": "Karta płatnicza",
    "qp.cardDesc": "Visa, Mastercard, inne",
    "qp.mobile": "Google Pay / Apple Pay",
    "qp.mobileDesc": "Płatność mobilna",
    "qp.formSubtitle": "Wypełnij formularz, załącz pliki i przejdź do płatności PayU.",
    "qp.contactData": "Dane kontaktowe",
    "qp.namePlaceholder": "Imię i nazwisko *",
    "qp.emailPlaceholder": "Adres e-mail *",
    "qp.phonePlaceholder": "Numer telefonu (opcjonalnie)",
    "qp.paymentDetails": "Szczegóły płatności",
    "qp.amountLabel": "Kwota do zapłaty (PLN) *",
    "qp.amountPlaceholder": "np. 150.00",
    "qp.transferTitleLabel": "Tytuł przelewu *",
    "qp.transferTitlePlaceholder": "np. Zamówienie naklejek #123",
    "qp.attachFiles": "Załącz pliki (opcjonalnie)",
    "qp.fileTypes": "PNG, JPG, PDF, SVG, TIFF, WEBP — maks. 20 MB / plik",
    "qp.uploading": "Przesyłanie...",
    "qp.clickToUpload": "Kliknij, aby wybrać pliki",
    "qp.orderNotes": "Uwagi do zamówienia",
    "qp.notesPlaceholder": "Opisz szczegóły zamówienia, rozmiary, ilości, rodzaj folii...",
    "qp.characters": "znaków",
    "qp.verification": "Weryfikacja",
    "qp.howMuch": "Ile to",
    "qp.enterResult": "Wpisz wynik",
    "qp.correct": "Poprawnie!",
    "qp.incorrect": "Nieprawidłowy wynik",
    "qp.submit": "Złóż zamówienie",
    "qp.processing": "Przetwarzam...",
    "qp.redirectInfo": "Po kliknięciu zostaniesz przekierowany do płatności PayU.",
    "qp.successTitle": "Zamówienie przyjęte!",
    "qp.successText": "Twoje zamówienie zostało przyjęte. Za chwilę nastąpi przekierowanie do płatności.",
    "qp.errorBot": "Wykryto podejrzaną aktywność.",
    "qp.errorTooFast": "Formularz wypełniony zbyt szybko.",
    "qp.errorCaptcha": "Nieprawidłowa odpowiedź na pytanie weryfikacyjne.",
    "qp.errorAmount": "Wpisz poprawną kwotę.",
    "qp.errorGeneral": "Wystąpił błąd przy tworzeniu płatności. Spróbuj ponownie.",
    "qp.orderPlaced": "Zamówienie zostało złożone!",

    // About
    "about.title": "O",
    "about.titleHighlight": "nas",
    "about.p1": "to marka należąca do DRUKEXPERT.PL Sp. z o.o. z siedzibą w Katowicach. Specjalizujemy się w druku najwyższej jakości naklejek na foliach specjalnych – holograficznych, brokatowych, lustrzanych, przezroczystych i wielu innych.",
    "about.p2": "Nasza drukarnia wykorzystuje najnowocześniejsze technologie druku cyfrowego, zapewniając doskonałą jakość kolorów i trwałość naklejek.",
    "about.p3": "Oferujemy zarówno naklejki kiss-cut, jak i die-cut. Szeroki wybór folii i laminatów pozwala stworzyć naklejki idealne do każdego zastosowania.",
    "about.stickersCut": "Stickers Cut to nasze darmowe narzędzie online do generowania konturu cięcia naklejek – tworzymy je i rozwijamy w Stickers Tattooo. Znajdziesz je pod adresem stickers-cut.com: wgrywasz grafikę, a narzędzie przygotowuje gotową linię cięcia w SVG lub PDF, którą od razu wykorzystasz w naszym kreatorze naklejek.",
    "about.companyData": "Dane firmy",
    "about.contact": "Kontakt",
    "about.instagramChat": "Czat na Instagramie",

    // Cart
    "cart.title": "Twój",
    "cart.titleHighlight": "Koszyk",
    "cart.empty": "Koszyk jest pusty",
    "cart.goToCreator": "Kreator naklejek",
    "cart.total": "Suma:",
    "cart.back": "Wróć",
    "cart.continueShopping": "Kontynuuj zakupy",
    "cart.goToPayment": "Przejdź do płatności",
    "cart.clearCart": "Wyczyść koszyk",
    "cart.cartCleared": "Koszyk wyczyszczony",
    "cart.discount_after_checkout": "Rabat powitalny za rejestrację zostanie naliczony po kliknięciu \u201ePrzejdź do płatności\u201d.",
    "cart.shippingMethod": "Forma przesyłki",
    "cart.loading": "Ładowanie...",
    "cart.products": "Produkty",
    "cart.shipping": "Przesyłka",
    "cart.gross": "brutto",
    "shipping.paczkomat_inpost": "Paczkomat InPost - Dostawa tylko w Polsce",
    "shipping.kurier_inpost": "Kurier InPost - Dostawa tylko w Polsce",
    "shipping.kurier_europa": "Kurier Europa DPD/DHL/FedEx",
    "shipping.paczkomat_europa": "Paczkomat InPost Europa",
    "shipping.odbior_osobisty": "Odbiór osobisty",
    "shipping.uk_fedex": "Kurier UK oraz kraje spoza Unii Europejskiej",
    "shipping.kurier_irlandia": "Kurier Irlandia - FedEx / DPD",
    "shipping.kurier_hiszpania": "Kurier Hiszpania FedEx",
    "shipping.kurier_grecja": "Kurier Grecja",
    "shipping.locker_title": "Paczkomat",
    "shipping.locker_hint_pl": "Wpisz numer paczkomatu InPost w Polsce (np. KRA010, WAW123M).",
    "shipping.locker_hint_eu": "Wpisz numer paczkomatu InPost w wybranym kraju europejskim (np. AT01A, DE123456).",
    "shipping.locker_placeholder_pl": "Numer paczkomatu PL (np. KRA010)",
    "shipping.locker_placeholder_eu": "Numer paczkomatu Europa (np. AT01A)",
    "shipping.enter_locker": "Wpisz numer lub nazwę paczkomatu!",

    // Account
    "account.title": "Moje konto",
    "account.logout": "Wyloguj",
    "account.tab.profile": "Dane",
    "account.tab.orders": "Zamówienia",
    "account.tab.saved": "Zapisane",
    "account.name": "Imię i nazwisko",
    "account.phone": "Telefon",
    "account.address": "Adres wysyłki",
    "account.city": "Miasto",
    "account.zip": "Kod pocztowy",
    "account.locker": "Numer paczkomatu",
    "account.locker_hint": "Zapisz swój ulubiony paczkomat, aby przyspieszyć składanie zamówień",
    "account.save": "Zapisz dane",
    "account.saving": "Zapisywanie...",
    "account.saved": "Dane zapisane!",
    "account.save_error": "Błąd zapisu",
    "account.loading": "Ładowanie...",
    "account.no_orders": "Brak zamówień",
    "account.order": "Zamówienie",
    "account.date": "Data",
    "account.status": "Status",
    "account.products": "Produkty",
    "account.quantity": "Ilość",
    "account.dimensions": "Wymiary",
    "account.foil": "Folia",
    "account.laminate": "Laminat",
    "account.total": "Razem",
    "account.download_pdf": "Pobierz PDF",
    "account.product_n": "Produkt",
    "account.no_configs": "Brak zapisanych konfiguracji",
    "account.no_configs_hint": "Zapisz konfigurację w kreatorze naklejek, aby ją tu zobaczyć",
    "account.use": "Użyj",
    "account.pcs": "szt.",
    "account.delete_error": "Błąd usuwania",
    "account.config_deleted": "Konfiguracja usunięta",
    "account.delete_account": "Usuń konto i dane",
    "account.delete_title": "Usunięcie konta",
    "account.delete_warning": "Ta operacja jest nieodwracalna. Zostaną usunięte:",
    "account.delete_item1": "Twój profil i dane osobowe",
    "account.delete_item2": "Zapisane konfiguracje naklejek",
    "account.delete_item3": "Konto logowania",
    "account.delete_orders_note": "Historia zamówień zostanie zachowana w celach rachunkowych.",
    "account.cancel": "Anuluj",
    "account.confirm_delete": "Tak, usuń konto",
    "account.deleting": "Usuwanie...",
    "account.deleted": "Konto zostało usunięte",
    "account.delete_failed": "Nie udało się usunąć konta",
    "account.gdpr_note": "Zgodnie z RODO masz prawo do usunięcia swoich danych osobowych",
    "account.status.new": "Nowe",
    "account.status.unpaid": "Zmiana plików / uwagi",
    "account.status.processing": "W realizacji",
    "account.status.shipped": "Wysłane",
    "account.status.completed": "Zakończone",
    "account.status.cancelled": "Anulowane",
    "account.pay_now": "Dokończ płatność",
    "account.delete_order": "Usuń zamówienie",

    // Auth
    "auth.login": "Zaloguj się",
    "auth.register": "Utwórz konto",
    "auth.reset": "Resetuj hasło",
    "auth.login_desc": "Zaloguj się aby zarządzać zamówieniami",
    "auth.register_desc": "Załóż konto i korzystaj z rabatów",
    "auth.reset_desc": "Wyślemy Ci link do zmiany hasła",
    "auth.google": "Kontynuuj z Google",
    "auth.or_email": "lub przez email",
    "auth.name_placeholder": "Imię i nazwisko",
    "auth.password_placeholder": "Hasło (min. 6 znaków)",
    "auth.loading": "Ładowanie...",
    "auth.submit_login": "Zaloguj się",
    "auth.submit_register": "Zarejestruj się",
    "auth.submit_reset": "Wyślij link",
    "auth.forgot": "Zapomniałeś hasła?",
    "auth.no_account": "Nie masz konta?",
    "auth.has_account": "Masz już konto?",
    "auth.back_login": "← Wróć do logowania",
    "auth.reset_sent": "Link do resetowania hasła został wysłany na Twój email!",
    "auth.error": "Błąd",
    "auth.register_error": "Błąd rejestracji",
    "auth.login_error": "Błąd logowania",
    "auth.google_error": "Błąd logowania Google",
    "auth.register_success": "Konto utworzone! Sprawdź email, aby potwierdzić rejestrację.",
    "auth.login_success": "Zalogowano!",
    "auth.new_password_title": "Nowe hasło",
    "auth.new_password_placeholder": "Nowe hasło (min. 6 znaków)",
    "auth.new_password_saving": "Zapisywanie...",
    "auth.new_password_submit": "Zmień hasło",
    "auth.new_password_error": "Błąd: ",
    "auth.new_password_success": "Hasło zmienione! Możesz się zalogować.",
    "auth.new_password_label": "Nowe hasło",
    "auth.confirm_password_label": "Potwierdź nowe hasło",
    "auth.confirm_password_placeholder": "Wpisz hasło ponownie",
    "auth.password_too_weak": "Hasło jest za słabe – musi mieć co najmniej 6 znaków",
    "auth.passwords_mismatch": "Hasła nie są identyczne",

    // Checkout
    "checkout.title": "Checkout",
    "checkout.personalData": "Dane osobowe",
    "checkout.firstName": "Imię",
    "checkout.lastName": "Nazwisko",
    "checkout.email": "E-mail",
    "checkout.phone": "Telefon",
    "checkout.deliveryAddress": "Adres dostawy",
    "checkout.street": "Ulica i numer",
    "checkout.zip": "Kod pocztowy",
    "checkout.city": "Miasto",
    "checkout.wantInvoice": "Chcę fakturę VAT",
    "checkout.nip": "NIP",
    "checkout.paymentMethod": "Metoda płatności",
    "checkout.products": "Produkty:",
    "checkout.orderAndPay": "Zamawiam i płacę",
    "checkout.emptyCart": "Koszyk jest pusty!",
    "checkout.orderSuccess": "Zamówienie złożone! Przekierowanie do płatności...",
    "checkout.totalToPay": "Razem do zapłaty",
    "checkout.orderNotes": "Uwagi do zamówienia",
    "checkout.orderNotesPlaceholder": "Wpisz ważne uwagi, np. specjalne instrukcje dotyczące projektu, dodatkowe informacje...",
    "checkout.characters": "znaków",
    "checkout.creditCard": "Karta płatnicza",
    "checkout.saveToProfile": "Zapamiętaj dane do przyszłych zamówień",
    "checkout.welcomeDiscount": "Rabat powitalny",
    "checkout.loyaltyUse": "Wykorzystaj punkty lojalnościowe",
    "checkout.loyaltyPts": "pkt",
    "checkout.loyaltyDiscount": "Rabat",
    "checkout.loyaltyMax50": "Max 50% wartości zamówienia",
    "checkout.processing": "Przetwarzanie...",
    "checkout.companyDataTitle": "Dane firmy z bazy GUS",
    "checkout.companyNameLabel": "Nazwa firmy",
    "checkout.loginRequired": "Musisz być zalogowany, aby złożyć zamówienie",
    "checkout.sessionExpired": "Sesja wygasła. Zaloguj się ponownie, aby dokończyć zamówienie.",
    "cart.printOffer": "Zapisz ofertę jako PDF",

    // Questions link
    "questions.text": "Masz pytania dotyczące działania kreatora naklejek?",
    "questions.link": "Napisz do nas",

    // Tool link
    "tool.badge": "✂️ Narzędzie online",
    "tool.title": "Darmowe narzędzie do konturów",
    "tool.description": "Stwórz wektor konturu do swojej naklejki — za darmo!",

    // White underprint hint
    "config.whiteHint": "Jak przygotować plik z białym poddrukiem?",
    "config.whiteHintLink": "Zobacz instrukcję w FAQ →",
    "config.whiteRecommend": "Do niektórych projektów polecamy włączenie białego poddruku — kolory będą bardziej żywe, kolory bez białego poddruku są permanentne jak witraż.",

    // Contact error
    "contact.error": "Nie udało się wysłać wiadomości. Spróbuj ponownie.",

    // Configurator data labels
    "shape.kiss-cut": "Naklejki nacinane na arkuszach (kiss-cut)",
    "shape.kiss-cut.desc": "Naklejki wycięte na arkuszu z łatwym odrywaniem",
    "shape.die-cut": "Naklejki wycinane na wylot (die-cut)",
    "shape.die-cut.desc": "Naklejki wycięte dokładnie po kształcie projektu",
    "foil.biala": "Folia Biała",
    "foil.biala-mocny": "Folia Biała Mocny Klej",
    "foil.przezroczysta": "Przeźroczysta",
    "foil.przezroczysta-bialy": "Przeźroczysta – naklejki z poddrukiem białym",
    "foil.ultra-przejrzysta": "Ultra przejrzysta / wysoki połysk",
    "foil.ultra-przejrzysta-bialy": "Ultra przejrzysta / wysoki połysk – z poddrukiem białym",
    "foil.holograficzna": "Holograficzna",
    "foil.holograficzna-bialy": "Holograficzna – z poddrukiem białym",
    "foil.holo-opalizowane": "Holo opalizowane świecące w nocy",
    "foil.holo-opalizowane-bialy": "Holo opalizowane świecące w nocy z poddrukiem białym",
    "foil.mini-brokat": "Mini Brokat holo",
    "foil.mini-brokat-bialy": "Mini Brokat holo – z poddrukiem białym",
    "foil.brokat-duzy": "Brokat duży holo",
    "foil.brokat-duzy-bialy": "Brokat duży holo – z poddrukiem białym",
    "foil.szklo-tluczone": "Szkło tłuczone holo",
    "foil.szklo-tluczone-bialy": "Szkło tłuczone holo – z poddrukiem białym",
    "foil.lustrzana": "Lustrzana srebrna (chrom)",
    "foil.lustrzana-bialy": "Lustrzana srebrna (chrom) – z poddrukiem białym",
    "foil.odblaskowe": "Folia odblaskowa / refleksyjna",
    "foil.szczotkowane-alu": "Szczotkowane aluminium",
    "foil.szczotkowane-alu-bialy": "Szczotkowane aluminium – z poddrukiem białym",
    "foil.plotno-canvas": "Naklejki na płótnie canvas",
    "foil.magnetyczne": "Naklejki magnetyczne",
    "foil.zielonkawa-glow": "Folia zielonkawa świecąca w nocy",
    "foil.zielonkawa-glow-bialy": "Folia zielonkawa świecąca w nocy – z poddrukiem białym",
    "lam.polysk": "Laminat połysk",
    "lam.ultra-polysk": "Laminat ultra przejrzysty / wysoki połysk",
    "lam.ultra-polysk-brokat": "Laminat ultra przejrzysty / wysoki połysk z Brokatem",
    "lam.mat": "Laminat mat",
    "lam.brokat-mat": "Laminat brokat matowy",
    "lam.krzyzyk-plotno": "Laminat krzyżykowy płótno",
    "lam.holo": "Laminat holo",
    "lam.holo-brokat": "Laminat holo brokat",
    "lam.holo-gwiazdki": "Laminat holo gwiazdki",
    "lam.holo-tluczone": "Laminat holo tłuczone szkło",
    "lam.holo-piksele": "Laminat holo piksele",
    "lam.skora": "Laminat o strukturze skóry",
    "lam.podlogowy": "Laminat podłogowy / matowy",
    "lam.holo-piasek-mini": "Laminat holo piasek z mini gwiazdkami",
    "lam.holo-duze-gwiazdki": "Laminat holo duże gwiazdki",
    "lam.holo-duze-piksele": "Laminat holo duże piksele",
    "lam.nielam-polysk": "Druk na folii wysoki połysk – naklejki nielaminowane",
    "lam.nielam-mat": "Druk na folii matowej – naklejki nielaminowane",
    "lam.bez-lam": "Bez laminowania",
    "lam.ultra-polysk-sztywny": "Laminat ultra przejrzysty / wysoki połysk – z folią ochronną, sztywny laminat",
    "lam.ultra-polysk-brokat-sztywny": "Laminat ultra przejrzysty / wysoki połysk z Brokatem – z folią ochronną, sztywny laminat",
    "lam.flatNote": "do stosowania na płaskie powierzchnie",

    // FAQ items
    "faq.q1": "Nie jestem pewien czy mój plik nadaje się do druku?",
    "faq.a1": "Wyślij nam plik, a nasz zespół sprawdzi go bezpłatnie i poinformuje Cię o ewentualnych poprawkach. Akceptujemy pliki w formatach PDF, AI, EPS, SVG oraz PNG/JPG w wysokiej rozdzielczości (min. 300 DPI; zalecamy min. 600 DPI).",
    "faq.q2": "Naklejki na przezroczystej folii – bez poddruku białego i z poddrukiem?",
    "faq.a2": "Naklejki bez poddruku białego będą półprzezroczyste – kolory będą widoczne, ale przebijać będzie tło. Naklejki z poddrukiem białym mają pod kolorami warstwę białego tuszu, która poprawia czytelność kolorów, ale w druku solwentowym pozostaje półtransparentna, dlatego na folii przezroczystej tło może częściowo prześwitywać.",
    "faq.q3": "Jak przygotować plik do druku?",
    "faq.a3": "Plik powinien być w formacie wektorowym (PDF, AI, EPS, SVG) lub rastrowym w rozdzielczości min. 300 DPI (zalecamy min. 600 DPI). Dodaj 2mm spadów wokół projektu. Kolory w CMYK. Linia cięcia powinna być zaznaczona na osobnej warstwie.",
    "faq.q4": "Potwierdzenie plików przed drukiem",
    "faq.a4": "Przed rozpoczęciem druku wysyłamy podgląd do akceptacji. Druk ruszamy dopiero po Twoim potwierdzeniu, że wszystko jest OK.",
    "faq.q5": "Jak złożyć zamówienie?",
    "faq.a5": "Skonfiguruj naklejki w kreatorze – wybierz kształt, rodzaj folii, laminat, wymiary i ilość. Następnie wgraj plik projektu, dodaj do koszyka i przejdź do realizacji zamówienia. Po opłaceniu zamówienia, przygotujemy Twoje naklejki.",
    "faq.q6": "Jaka jest różnica między naklejkami die-cut a kiss-cut?",
    "faq.a6": "Die-cut – naklejka wycięta dokładnie po kształcie projektu, bez dodatkowego tła. Kiss-cut – naklejka nacięta na arkuszu, łatwa do odrywania, z zachowanym prostokątnym tłem wokół projektu. Kiss-cut idealne do rozdawania, die-cut – do bezpośredniego naklejania.",
    "faq.q7": "Czy oferujecie naklejki przezroczyste, holograficzne lub metalizowane z białym poddrukiem?",
    "faq.a7": "Tak! Oferujemy szeroki wybór folii specjalnych z opcją poddruku białego: przezroczyste, holograficzne, brokatowe, lustrzane (chrom), szczotkowane aluminium i wiele innych. Biały poddruk poprawia widoczność kolorów, ale w druku solwentowym nie daje pełnego krycia – na foliach przezroczystych tło może prześwitywać.",
    "faq.q8": "Jak przygotować biały poddruk?",
    "faq.a8": "Biały poddruk należy przygotować jako osobną warstwę w pliku. Warstwa ta powinna być nazwana 'White' lub 'Biały poddruk'. Wszystkie elementy, które mają mieć poddruk biały, zaznacz na tej warstwie kolorem 100% Spot White.",
    "faq.q9": "Moje zamówienie nie zostało wydrukowane prawidłowo, co mam zrobić?",
    "faq.a9": "Skontaktuj się z nami niezwłocznie, wysyłając zdjęcia wadliwych naklejek na nasz e-mail. Rozpatrzymy reklamację i zaproponujemy rozwiązanie – ponowny druk lub zwrot kosztów.",
    "faq.q10": "Ile czasu potrwa rozpatrzenie reklamacji?",
    "faq.a10": "Reklamacje rozpatrujemy w ciągu 3–5 dni roboczych od otrzymania zgłoszenia ze zdjęciami.",
    "faq.q11": "Kiedy otrzymam moje zamówienie?",
    "faq.a11": "Standardowy czas realizacji to 3–5 dni roboczych od momentu akceptacji pliku i opłacenia zamówienia. Wysyłka kurierem DPD lub InPost trwa dodatkowo 1–2 dni robocze.",
    "faq.q12": "Jakie są dane firmy i dane do płatności?",
    "faq.a12": "STICKERS TATTOOO / DRUKEXPERT.PL Sp. z o.o., ul. Głogowa 39, 40-750 Katowice. NIP i pozostałe dane dostępne na fakturze.",
    "faq.q13": "Czy wystawiamy faktury VAT?",
    "faq.a13": "Tak, wystawiamy faktury VAT. Zaznacz opcję 'Faktura VAT' podczas składania zamówienia i podaj dane do faktury.",
    "faq.q14": "Źle obliczone zamówienie, wymagające dopłaty.",
    "faq.a14": "Jeśli po weryfikacji pliku okaże się, że wymiary naklejek są inne niż podane w zamówieniu, skontaktujemy się z Tobą w sprawie ewentualnej dopłaty lub korekty zamówienia.",

    // Calculator
    "calc.badge": "Kalkulator",
    "calc.title": "Sprawdź",
    "calc.titleHighlight": "cenę",
    "calc.subtitle": "Przeciągnij suwaki i zobacz cenę naklejek w czasie rzeczywistym",
    "calc.discount": "Rabat ilościowy",
    "calc.unitPrice": "Cena za sztukę",
    "calc.totalPrice": "Cena łączna",
    "calc.note": "Cena orientacyjna dla Folii Białej die-cut z laminatem. Dokładna cena w konfiguratorze.",
    "calc.noteGeneric": "Cena orientacyjna dla wybranej folii i laminatu. Dokładna cena w konfiguratorze.",
    "calc.pcs": "szt.",

    // Inspirations
    "insp.badge": "Portfolio",
    "insp.title": "Nasze",
    "insp.titleHighlight": "realizacje",
    "insp.subtitle": "Zobacz, jak nasze naklejki wyglądają w różnych branżach",
    "insp.industry.all": "Wszystkie",
    "insp.industry.gastronomia": "Gastronomia",
    "insp.industry.kosmetyki": "Kosmetyki",
    "insp.industry.IT": "IT / Tech",
    "insp.industry.moda": "Moda",
    "insp.industry.motoryzacja": "Motoryzacja",
    "insp.industry.inne": "Inne",

    // Reviews
    "review.badge": "Opinie",
    "review.title": "Co mówią",
    "review.titleHighlight": "nasi klienci",
    "review.reviewsCount": "opinii",
    "review.noReviews": "Bądź pierwszym, który wystawi opinię!",
    "review.addReview": "Dodaj opinię",
    "review.addReviewTitle": "Dodaj swoją opinię",
    "review.yourName": "Twoje imię",
    "review.namePlaceholder": "Jan K.",
    "review.yourRating": "Ocena",
    "review.yourReview": "Twoja opinia",
    "review.reviewPlaceholder": "Podziel się swoim doświadczeniem z naszymi naklejkami...",
    "review.send": "Wyślij opinię",
    "review.sending": "Wysyłanie...",
    "review.fillAll": "Wypełnij imię i treść opinii",
    "review.loginRequired": "Zaloguj się, aby dodać opinię",
    "review.submitted": "Dziękujemy! Opinia zostanie opublikowana po weryfikacji.",
    "review.alreadySubmitted": "Możesz wystawić tylko jedną opinię.",
    "review.moderationNote": "Opinia zostanie opublikowana po weryfikacji przez nasz zespół",

    // Loyalty
    "loyalty.title": "Program lojalnościowy",
    "loyalty.subtitle": "Zbieraj punkty za zamówienia",
    "loyalty.points": "punktów",
    "loyalty.worth": "Wartość rabatu:",
    "loyalty.minRedeem": "Minimalna ilość do wymiany",
    "loyalty.earnInfo": "Za każde wydane {rate} zł otrzymujesz 1 punkt. Punkty wymieniasz na rabat przy kolejnym zamówieniu!",
    "loyalty.history": "Historia punktów",
    "loyalty.howItWorks": "Jak to działa?",
    "loyalty.rule1": "Za każde wydane {rate} zł w zamówieniu dostajesz 1 punkt.",
    "loyalty.rule2": "Każdy punkt jest wart {value} PLN rabatu.",
    "loyalty.rule3": "Punkty możesz wymienić na rabat, gdy uzbierasz min. {min} pkt.",
    "loyalty.example": "📌 Przykład:",
    "loyalty.exampleCalc": "Zamówienie za {amount} zł → dostajesz {pts} pkt → wartość rabatu: {value} PLN. Czyli 100 pkt = 5 zł rabatu!",
    "loyalty.reviewBonus": "Dodaj opinię i zgarnij 100 punktów (= 5 zł rabatu)!",
    "account.tab.loyalty": "Punkty",

    // UI labels
    "ui.changeTheme": "Zmień motyw",
    "ui.hideGhost": "Ukryj duszka",
    "ui.unlockGhost": "Odblokuj duszka",
    "ui.lockGhost": "Zablokuj duszka w rogu",
    "config.qtyPlaceholder": "np. 250",
    "config.customOrderNote": "W przypadku bardzo dużych lub niestandardowych zamówień przygotowujemy wycenę indywidualną. W takiej sytuacji prosimy o kontakt mailowy",
    "config.customOrderNoteOr": "lub",
    "config.minReached": "Kwota minimalna osiągnięta",
    "config.canOrder": "możesz zamówić!",
    "config.minNeeded": "brakuje",

    // SEO
    "seo.faq.title": "FAQ – Pytania o druk naklejek | Stickers Tattooo",
    "seo.faq.description": "Odpowiedzi na najczęściej zadawane pytania o druk naklejek: folie, laminaty, formaty plików, czas realizacji, wysyłka. Drukarnia Stickers Tattooo.",
    "seo.blog.title": "Blog o druku naklejek – Poradniki | Stickers Tattooo",
    "seo.blog.description": "Blog drukarni naklejek Stickers Tattooo. Poradniki: jak przygotować plik do druku, rodzaje folii, laminatów, naklejki holograficzne, die-cut i więcej.",
    "seo.contact.title": "Kontakt – Drukarnia Naklejek Stickers Tattooo | Katowice",
    "seo.contact.description": "Skontaktuj się z drukarnią naklejek Stickers Tattooo. Tel: +48 534 900 620, e-mail: stickers.tattooo@wp.pl. Katowice, ul. Głogowa 39. Poznaj naszą firmę!",
    "seo.about.title": "O nas – Drukarnia Naklejek Stickers Tattooo | Katowice",
    "seo.about.description": "Poznaj Stickers Tattooo – drukarnia naklejek premium z Katowic. Drukujemy naklejki foliowe, holograficzne i die-cut od 2020 roku. Sprawdź naszą ofertę!",
    "seo.foils.title": "Rodzaje folii do naklejek – 15 folii | Stickers Tattooo",
    "seo.foils.description": "Porównanie 15 rodzajów folii do druku naklejek: białe, przezroczyste, holograficzne, brokatowe, lustrzane chrom, odblaskowe, magnetyczne, świecące w nocy.",
    "seo.breadcrumb.home": "Strona główna",
  },

  en: {
    "nav.configurator": "Sticker Creator",
    "nav.quickPayment": "Quick Payment",
    "nav.blog": "Blog",
    "nav.foils": "Foils & Laminates",
    "nav.faq": "FAQ",
    "nav.contact": "Contact / About",
    "nav.about": "About Us",
    "nav.portfolio": "Portfolio",
    "nav.cart": "Cart",
    "nav.loggedIn": "Logged in",

    "hero.badge": "Premium Stickers",
    "hero.followUs": "Click to follow us →",
    "hero.description": "Premium quality stickers – holographic, glitter, transparent, opalescent and glow-in-the-dark and many more. Online printing. Configure and order in a minute.",
    "hero.cta": "Create Stickers",
    "welcome.title": "−10% welcome discount",
    "welcome.description": "One-time discount for new customers on first orders up to ≈ {eur} €",

    "config.title": "Sticker",
    "config.titleHighlight": "Parameters",
    "config.shape": "Shape",
    "config.foil": "Foil Type",
    "config.foilShort": "Foil type – base on which stickers are printed",
    "config.laminate": "Laminate Type",
    "config.laminateShort": "Laminate type – film covering the stickers",
    "config.dimensions": "Dimensions",
    "config.width": "Width",
    "config.height": "Height",
    "config.unit.mm": "millimeters",
    "config.unit.cm": "centimeters",
    "config.quantity": "Quantity",
    "config.file": "Project File",
    "config.filePrompt": "Click to upload files (PDF, PNG, TIFF, JPG, BMP)",
    "config.fileSelected": "Selected:",
    "config.fileOver10": "Files over 20 MB please send directly to",
    "config.orVia": "or via",
    "config.calculate": "Calculate Price",
    "config.summary": "Order Summary",
    "config.price": "Price:",
    "config.discount": "✓ Quantity discount applied",
    "config.warning": "⚠ Please make thoughtful purchases.",
    "config.warningText": "If you're unsure how stickers will look — especially those printed on transparent foil, or stickers without white underprint on holographic and metallic foils — please check the",
    "config.warningFaq": "FAQ section",
    "config.warningBlogPrefix": "posts on our",
    "config.warningBlog": "blog",
    "config.warningGhost": "ask the ghost",
    "config.warningOr": "or contact us with a specific question.",
    "config.warningText2": "Stickers ordered through the creator are printed exactly as you designed them. If after receiving the shipment you find that the appearance does not meet your expectations due to not reviewing product information beforehand, complaints will not be accepted.",
    
    "config.addToCart": "Add to Cart",
    "config.uploaded": "uploaded...",
    "config.remove": "remove",
    "config.pcs": "pcs",
    "config.pieces": "pieces",
    "config.shipDate": "Shipping by",
    "config.files": "Files",
    "config.minOrder": "Min. order for this foil:",
    "config.minOrderMissing": "missing",
    "config.whitePrintLabel": "With white underprint",
    "config.whitePrintSuffix": " – with white underprint",
    "config.whitePrintDropdown": " · ⬤ white print",

    "gallery.title": "Our",
    "gallery.titleHighlight": "Work",
    "gallery.description": "See the stickers we created for our clients",
    "gallery.preparing": "Gallery in preparation",
    "gallery.soon": "Photos and videos coming soon",

    "faq.title": "Frequently Asked",
    "faq.titleHighlight": "Questions",
    "faq.showAll": "See all questions",

    "contact.title": "Write to",
    "contact.titleHighlight": "Us",
    "contact.description": "We know everything about stickers! Write to us and we'll answer all your questions.",
    "contact.name": "First Name",
    "contact.surname": "Last Name",
    "contact.email": "Email",
    "contact.phone": "Phone number",
    "contact.message": "Message",
    "contact.send": "Send",
    "contact.success": "Message sent! We'll reply as soon as possible.",

    "footer.contact": "Contact",
    "footer.info": "Information",
    "footer.stickersCut": "Stickers Cut – our free cut-contour generator",
    "footer.faq": "FAQ",
    "footer.terms": "Terms & Conditions",
    "footer.privacy": "Privacy Policy",
    "footer.cookies": "Cookie Policy",
    "footer.withdrawal": "Right of Withdrawal",
    "footer.rights": "All rights reserved.",

    "cookie.message": "We use cookies only for technical purposes (session, cart, preferences). We do not use marketing or analytics cookies.",
    "cookie.learnMore": "Learn more about our cookie policy",
    "cookie.accept": "Got it",

    "blog.title": "Sticker",
    "blog.titleHighlight": "Guide",
    "blog.description": "Expert articles about sticker printing, foils, laminates and file preparation",
    "blog.readMore": "Read more",
    "blog.comingSoon": "Articles coming soon",
    "blog.allArticles": "All articles",
    "blog.notFound": "Article not found",
    "blog.backToBlog": "← Back to blog",

    "qp.title": "Quick",
    "qp.titleHighlight": "Payment",
    "qp.description": "Choose a payment method and pay in seconds",
    "qp.info": "Payments powered by Przelewy24. All transactions are encrypted and secure.",
    "qp.blik": "BLIK",
    "qp.blikDesc": "Fast payment with BLIK code",
    "qp.transfer": "Przelewy24",
    "qp.transferDesc": "Online transfer via Przelewy24",
    "qp.card": "Credit/Debit Card",
    "qp.cardDesc": "Visa, Mastercard, others",
    "qp.mobile": "Google Pay / Apple Pay",
    "qp.mobileDesc": "Mobile payment",
    "qp.formSubtitle": "Fill in the form, attach files and proceed to PayU payment.",
    "qp.contactData": "Contact details",
    "qp.namePlaceholder": "Full name *",
    "qp.emailPlaceholder": "Email address *",
    "qp.phonePlaceholder": "Phone number (optional)",
    "qp.paymentDetails": "Payment details",
    "qp.amountLabel": "Amount to pay (PLN) *",
    "qp.amountPlaceholder": "e.g. 150.00",
    "qp.transferTitleLabel": "Transfer title *",
    "qp.transferTitlePlaceholder": "e.g. Sticker order #123",
    "qp.attachFiles": "Attach files (optional)",
    "qp.fileTypes": "PNG, JPG, PDF, SVG, TIFF, WEBP — max. 20 MB / file",
    "qp.uploading": "Uploading...",
    "qp.clickToUpload": "Click to select files",
    "qp.orderNotes": "Order notes",
    "qp.notesPlaceholder": "Describe order details, sizes, quantities, foil type...",
    "qp.characters": "characters",
    "qp.verification": "Verification",
    "qp.howMuch": "What is",
    "qp.enterResult": "Enter result",
    "qp.correct": "Correct!",
    "qp.incorrect": "Incorrect result",
    "qp.submit": "Place order",
    "qp.processing": "Processing...",
    "qp.redirectInfo": "After clicking you will be redirected to PayU payment.",
    "qp.successTitle": "Order received!",
    "qp.successText": "Your order has been received. You will be redirected to payment shortly.",
    "qp.errorBot": "Suspicious activity detected.",
    "qp.errorTooFast": "Form filled too quickly.",
    "qp.errorCaptcha": "Incorrect verification answer.",
    "qp.errorAmount": "Enter a valid amount.",
    "qp.errorGeneral": "An error occurred while creating payment. Please try again.",
    "qp.orderPlaced": "Order placed!",

    "about.title": "About",
    "about.titleHighlight": "Us",
    "about.p1": "is a brand owned by DRUKEXPERT.PL Sp. z o.o. based in Katowice, Poland. We specialize in printing the highest quality stickers on special foils – holographic, glitter, mirror, transparent and many more.",
    "about.p2": "Our printing house uses the latest digital printing technologies, ensuring excellent color quality and sticker durability.",
    "about.p3": "We offer both kiss-cut and die-cut stickers. A wide selection of foils and laminates allows you to create stickers perfect for any application.",
    "about.stickersCut": "Stickers Cut is our free online tool for generating sticker cut contours – built and maintained by Stickers Tattooo. You will find it at stickers-cut.com: upload your artwork and the tool prepares a ready cut line in SVG or PDF that you can use straight away in our sticker creator.",
    "about.companyData": "Company Details",
    "about.contact": "Contact",
    "about.instagramChat": "Chat on Instagram",

    "cart.title": "Your",
    "cart.titleHighlight": "Cart",
    "cart.empty": "Cart is empty",
    "cart.goToCreator": "Sticker Creator",
    "cart.total": "Total:",
    "cart.back": "Back",
    "cart.continueShopping": "Continue Shopping",
    "cart.goToPayment": "Proceed to Payment",
    "cart.clearCart": "Clear cart",
    "cart.cartCleared": "Cart cleared",
    "cart.discount_after_checkout": "Welcome discount for registration will be applied after clicking \"Proceed to Payment\".",
    "cart.shippingMethod": "Shipping method",
    "cart.loading": "Loading...",
    "cart.products": "Products",
    "cart.shipping": "Shipping",
    "cart.gross": "gross",
    "shipping.paczkomat_inpost": "InPost Parcel Locker - delivery in Poland only",
    "shipping.kurier_inpost": "InPost Courier - delivery in Poland only",
    "shipping.kurier_europa": "Europe Courier DPD/DHL/FedEx",
    "shipping.paczkomat_europa": "InPost Parcel Locker Europe",
    "shipping.odbior_osobisty": "Personal pickup",
    "shipping.uk_fedex": "Courier UK & non-EU European countries",
    "shipping.kurier_irlandia": "Courier Ireland - FedEx / DPD",
    "shipping.kurier_hiszpania": "Courier Spain FedEx",
    "shipping.kurier_grecja": "Courier Greece",
    "shipping.locker_title": "Parcel Locker",
    "shipping.locker_hint_pl": "Enter your InPost parcel locker number in Poland (e.g. KRA010, WAW123M).",
    "shipping.locker_hint_eu": "Enter your InPost parcel locker number in your European country (e.g. AT01A, DE123456).",
    "shipping.locker_placeholder_pl": "Locker number PL (e.g. KRA010)",
    "shipping.locker_placeholder_eu": "Locker number Europe (e.g. AT01A)",
    "shipping.enter_locker": "Please enter a parcel locker number!",

    // Account
    "account.title": "My Account",
    "account.logout": "Sign Out",
    "account.tab.profile": "Profile",
    "account.tab.orders": "Orders",
    "account.tab.saved": "Saved",
    "account.name": "Full name",
    "account.phone": "Phone",
    "account.address": "Shipping address",
    "account.city": "City",
    "account.zip": "Postal code",
    "account.locker": "Parcel locker number",
    "account.locker_hint": "Save your favourite locker to speed up ordering",
    "account.save": "Save",
    "account.saving": "Saving...",
    "account.saved": "Data saved!",
    "account.save_error": "Save error",
    "account.loading": "Loading...",
    "account.no_orders": "No orders",
    "account.order": "Order",
    "account.date": "Date",
    "account.status": "Status",
    "account.products": "Products",
    "account.quantity": "Quantity",
    "account.dimensions": "Dimensions",
    "account.foil": "Foil",
    "account.laminate": "Laminate",
    "account.total": "Total",
    "account.download_pdf": "Download PDF",
    "account.product_n": "Product",
    "account.no_configs": "No saved configurations",
    "account.no_configs_hint": "Save a configuration in the sticker creator to see it here",
    "account.use": "Use",
    "account.pcs": "pcs",
    "account.delete_error": "Delete error",
    "account.config_deleted": "Configuration deleted",
    "account.delete_account": "Delete account & data",
    "account.delete_title": "Delete Account",
    "account.delete_warning": "This action is irreversible. The following will be deleted:",
    "account.delete_item1": "Your profile and personal data",
    "account.delete_item2": "Saved sticker configurations",
    "account.delete_item3": "Login account",
    "account.delete_orders_note": "Order history will be retained for accounting purposes.",
    "account.cancel": "Cancel",
    "account.confirm_delete": "Yes, delete account",
    "account.deleting": "Deleting...",
    "account.deleted": "Account has been deleted",
    "account.delete_failed": "Failed to delete account",
    "account.gdpr_note": "Under GDPR you have the right to delete your personal data",
    "account.status.new": "New",
    "account.status.unpaid": "File changes / notes",
    "account.status.processing": "Processing",
    "account.status.shipped": "Shipped",
    "account.status.completed": "Completed",
    "account.status.cancelled": "Cancelled",
    "account.pay_now": "Complete payment",
    "account.delete_order": "Delete order",

    // Auth
    "auth.login": "Sign In",
    "auth.register": "Create Account",
    "auth.reset": "Reset Password",
    "auth.login_desc": "Sign in to manage your orders",
    "auth.register_desc": "Create an account and enjoy discounts",
    "auth.reset_desc": "We'll send you a password reset link",
    "auth.google": "Continue with Google",
    "auth.or_email": "or via email",
    "auth.name_placeholder": "Full name",
    "auth.password_placeholder": "Password (min. 6 characters)",
    "auth.loading": "Loading...",
    "auth.submit_login": "Sign In",
    "auth.submit_register": "Sign Up",
    "auth.submit_reset": "Send link",
    "auth.forgot": "Forgot your password?",
    "auth.no_account": "Don't have an account?",
    "auth.has_account": "Already have an account?",
    "auth.back_login": "← Back to sign in",
    "auth.reset_sent": "Password reset link has been sent to your email!",
    "auth.error": "Error",
    "auth.register_error": "Registration error",
    "auth.login_error": "Login error",
    "auth.google_error": "Google login error",
    "auth.register_success": "Account created! Check your email to confirm registration.",
    "auth.login_success": "Signed in!",
    "auth.new_password_title": "New Password",
    "auth.new_password_placeholder": "New password (min. 6 characters)",
    "auth.new_password_saving": "Saving...",
    "auth.new_password_submit": "Change password",
    "auth.new_password_error": "Error: ",
    "auth.new_password_success": "Password changed! You can now sign in.",
    "auth.new_password_label": "New password",
    "auth.confirm_password_label": "Confirm new password",
    "auth.confirm_password_placeholder": "Re-enter your password",
    "auth.password_too_weak": "Password is too weak – must be at least 6 characters",
    "auth.passwords_mismatch": "Passwords do not match",

    "checkout.title": "Checkout",
    "checkout.personalData": "Personal Data",
    "checkout.firstName": "First Name",
    "checkout.lastName": "Last Name",
    "checkout.email": "E-mail",
    "checkout.phone": "Phone",
    "checkout.deliveryAddress": "Delivery Address",
    "checkout.street": "Street and number",
    "checkout.zip": "Postal code",
    "checkout.city": "City",
    "checkout.wantInvoice": "I want a VAT invoice",
    "checkout.nip": "Tax ID",
    "checkout.paymentMethod": "Payment Method",
    "checkout.products": "Products:",
    "checkout.orderAndPay": "Order and Pay",
    "checkout.emptyCart": "Cart is empty!",
    "checkout.orderSuccess": "Order placed! Redirecting to payment...",
    "checkout.totalToPay": "Total to pay",
    "checkout.orderNotes": "Order notes",
    "checkout.orderNotesPlaceholder": "Enter important notes, e.g. special instructions for the project, additional information...",
    "checkout.characters": "characters",
    "checkout.creditCard": "Credit Card",
    "checkout.saveToProfile": "Remember details for future orders",
    "checkout.welcomeDiscount": "Welcome discount",
    "checkout.loyaltyUse": "Use loyalty points",
    "checkout.loyaltyPts": "pts",
    "checkout.loyaltyDiscount": "Discount",
    "checkout.loyaltyMax50": "Max 50% of order value",
    "checkout.processing": "Processing...",
    "checkout.companyDataTitle": "Company data from GUS registry",
    "checkout.companyNameLabel": "Company name",
    "checkout.loginRequired": "You must be logged in to place an order",
    "checkout.sessionExpired": "Your session has expired. Please log in again to complete your order.",
    "cart.printOffer": "Save offer as PDF",

    "questions.text": "Questions about how the sticker creator works?",
    "questions.link": "Contact us",

    "tool.badge": "✂️ Online Tool",
    "tool.title": "Free contour tool",
    "tool.description": "Create a contour vector for your sticker — for free!",

    "config.whiteHint": "How to prepare a file with white underprint?",
    "config.whiteHintLink": "See instructions in FAQ →",
    "config.whiteRecommend": "For some designs we recommend enabling white underprint — colors will be more vivid and readable on a transparent background.",

    "contact.error": "Failed to send the message. Please try again.",

    "shape.kiss-cut": "Kiss-cut stickers (on sheets)",
    "shape.kiss-cut.desc": "Stickers cut on a sheet with easy peeling",
    "shape.die-cut": "Die-cut stickers",
    "shape.die-cut.desc": "Stickers cut exactly to the shape of the design",
    "foil.biala": "White Foil",
    "foil.biala-mocny": "White Foil Strong Adhesive",
    "foil.przezroczysta": "Transparent",
    "foil.przezroczysta-bialy": "Transparent – with white underprint",
    "foil.ultra-przejrzysta": "Ultra clear / high gloss",
    "foil.ultra-przejrzysta-bialy": "Ultra clear / high gloss – with white underprint",
    "foil.holograficzna": "Holographic",
    "foil.holograficzna-bialy": "Holographic – with white underprint",
    "foil.holo-opalizowane": "Holo opalescent glow-in-the-dark",
    "foil.holo-opalizowane-bialy": "Holo opalescent glow-in-the-dark with white underprint",
    "foil.mini-brokat": "Mini Glitter holo",
    "foil.mini-brokat-bialy": "Mini Glitter holo – with white underprint",
    "foil.brokat-duzy": "Large Glitter holo",
    "foil.brokat-duzy-bialy": "Large Glitter holo – with white underprint",
    "foil.szklo-tluczone": "Crushed glass holo",
    "foil.szklo-tluczone-bialy": "Crushed glass holo – with white underprint",
    "foil.lustrzana": "Mirror silver (chrome)",
    "foil.lustrzana-bialy": "Mirror silver (chrome) – with white underprint",
    "foil.odblaskowe": "Reflective / retroreflective foil",
    "foil.szczotkowane-alu": "Brushed aluminum",
    "foil.szczotkowane-alu-bialy": "Brushed aluminum – with white underprint",
    "foil.plotno-canvas": "Canvas stickers",
    "foil.magnetyczne": "Magnetic stickers",
    "foil.zielonkawa-glow": "Green glow-in-the-dark foil",
    "foil.zielonkawa-glow-bialy": "Green glow-in-the-dark foil – with white underprint",
    "lam.polysk": "Gloss laminate",
    "lam.ultra-polysk": "Ultra clear / high gloss laminate",
    "lam.ultra-polysk-brokat": "Ultra clear / high gloss laminate with Glitter",
    "lam.mat": "Matte laminate",
    "lam.brokat-mat": "Matte glitter laminate",
    "lam.krzyzyk-plotno": "Crosshatch canvas laminate",
    "lam.holo": "Holo laminate",
    "lam.holo-brokat": "Holo glitter laminate",
    "lam.holo-gwiazdki": "Holo stars laminate",
    "lam.holo-tluczone": "Holo crushed glass laminate",
    "lam.holo-piksele": "Holo pixels laminate",
    "lam.skora": "Leather texture laminate",
    "lam.podlogowy": "Floor / matte laminate",
    "lam.holo-piasek-mini": "Holo sand with mini stars laminate",
    "lam.holo-duze-gwiazdki": "Holo big stars laminate",
    "lam.holo-duze-piksele": "Holo big pixels laminate",
    "lam.nielam-polysk": "High-gloss vinyl print – unlaminated stickers",
    "lam.nielam-mat": "Matte vinyl print – unlaminated stickers",
    "lam.bez-lam": "No lamination",
    "lam.ultra-polysk-sztywny": "Ultra clear / high gloss laminate – with protective film, rigid laminate",
    "lam.ultra-polysk-brokat-sztywny": "Ultra clear / high gloss laminate with Glitter – with protective film, rigid laminate",
    "lam.flatNote": "for use on flat surfaces",

    "faq.q1": "I'm not sure if my file is suitable for printing?",
    "faq.a1": "Send us your file and our team will check it for free and inform you of any corrections needed. We accept files in PDF, AI, EPS, SVG and high-resolution PNG/JPG (min. 300 DPI; we recommend 600 DPI).",
    "faq.q2": "Stickers on transparent foil – without and with white underprint?",
    "faq.a2": "Stickers without white underprint will be semi-transparent – colors will be visible but the background will show through. Stickers with white underprint have a layer of white ink under the colors that improves color readability, but in solvent printing it remains semi-transparent, so on transparent foil the background may show through slightly.",
    "faq.q3": "How to prepare a file for printing?",
    "faq.a3": "The file should be in vector format (PDF, AI, EPS, SVG) or raster at min. 300 DPI (we recommend 600 DPI). Add 2mm bleed around the design. Colors in CMYK. The cut line should be marked on a separate layer.",
    "faq.q4": "File confirmation before printing",
    "faq.a4": "Before printing, we send a preview for approval. Printing starts only after your confirmation that everything is OK.",
    "faq.q5": "How to place an order?",
    "faq.a5": "Configure your stickers in the creator – choose shape, foil type, laminate, dimensions and quantity. Then upload your design file, add to cart and proceed to checkout. After payment, we'll prepare your stickers.",
    "faq.q6": "What is the difference between die-cut and kiss-cut stickers?",
    "faq.a6": "Die-cut – sticker cut exactly to the shape of the design, without extra background. Kiss-cut – sticker cut on a sheet, easy to peel off, with a rectangular background around the design. Kiss-cut is ideal for giveaways, die-cut – for direct application.",
    "faq.q7": "Do you offer transparent, holographic or metallic stickers with white underprint?",
    "faq.a7": "Yes! We offer a wide selection of special foils with white underprint option: transparent, holographic, glitter, mirror (chrome), brushed aluminum and many more. White underprint improves color visibility, but in solvent printing it does not provide full coverage – on transparent foils the background may show through.",
    "faq.q8": "How to prepare white underprint?",
    "faq.a8": "White underprint should be prepared as a separate layer in the file. The layer should be named 'White' or 'White underprint'. Mark all elements that should have white underprint with 100% Spot White color.",
    "faq.q9": "My order was not printed correctly, what should I do?",
    "faq.a9": "Contact us immediately, sending photos of the defective stickers to our email. We'll review the complaint and propose a solution – reprint or refund.",
    "faq.q10": "How long will it take to process a complaint?",
    "faq.a10": "We process complaints within 3–5 business days from receiving the report with photos.",
    "faq.q11": "When will I receive my order?",
    "faq.a11": "Standard production time is 3–5 business days from file approval and payment. Shipping via DPD or InPost courier takes an additional 1–2 business days.",
    "faq.q12": "What are the company details and payment information?",
    "faq.a12": "STICKERS TATTOOO / DRUKEXPERT.PL Sp. z o.o., ul. Głogowa 39, 40-750 Katowice. Tax ID and other details available on the invoice.",
    "faq.q13": "Do you issue VAT invoices?",
    "faq.a13": "Yes, we issue VAT invoices. Select the 'VAT Invoice' option when placing your order and provide your billing details.",
    "faq.q14": "Incorrectly calculated order requiring additional payment.",
    "faq.a14": "If after file verification it turns out that the sticker dimensions differ from those in the order, we'll contact you regarding any additional payment or order correction.",

    "calc.badge": "Calculator",
    "calc.title": "Check",
    "calc.titleHighlight": "the price",
    "calc.subtitle": "Drag the sliders and see sticker prices in real time",
    "calc.discount": "Volume discount",
    "calc.unitPrice": "Price per unit",
    "calc.totalPrice": "Total price",
    "calc.note": "Estimated price for White Foil die-cut with laminate. Exact price in the configurator.",
    "calc.noteGeneric": "Estimated price for the selected foil and laminate. Exact price in the configurator.",
    "calc.pcs": "pcs",
    "insp.badge": "Portfolio",
    "insp.title": "Our",
    "insp.titleHighlight": "creations",
    "insp.subtitle": "See how our stickers look in various industries",
    "insp.industry.all": "All",
    "insp.industry.gastronomia": "Gastronomy",
    "insp.industry.kosmetyki": "Cosmetics",
    "insp.industry.IT": "IT / Tech",
    "insp.industry.moda": "Fashion",
    "insp.industry.motoryzacja": "Automotive",
    "insp.industry.inne": "Other",
    "review.badge": "Reviews",
    "review.title": "What our",
    "review.titleHighlight": "customers say",
    "review.reviewsCount": "reviews",
    "review.noReviews": "Be the first to leave a review!",
    "review.addReview": "Add review",
    "review.addReviewTitle": "Add your review",
    "review.yourName": "Your name",
    "review.namePlaceholder": "John D.",
    "review.yourRating": "Rating",
    "review.yourReview": "Your review",
    "review.reviewPlaceholder": "Share your experience with our stickers...",
    "review.send": "Submit review",
    "review.sending": "Sending...",
    "review.fillAll": "Please fill in your name and review",
    "review.loginRequired": "Please log in to add a review",
    "review.submitted": "Thank you! Your review will be published after verification.",
    "review.alreadySubmitted": "You can only submit one review.",
    "review.moderationNote": "Your review will be published after verification by our team",
    "loyalty.title": "Loyalty program",
    "loyalty.subtitle": "Earn points with orders",
    "loyalty.points": "points",
    "loyalty.worth": "Discount value:",
    "loyalty.minRedeem": "Minimum to redeem",
    "loyalty.earnInfo": "For every {rate} PLN spent you earn 1 point. Redeem points for a discount on your next order!",
    "loyalty.history": "Points history",
    "loyalty.howItWorks": "How does it work?",
    "loyalty.rule1": "For every {rate} PLN spent you earn 1 point.",
    "loyalty.rule2": "Each point is worth {value} PLN discount.",
    "loyalty.rule3": "You can redeem points for a discount once you collect at least {min} pts.",
    "loyalty.example": "📌 Example:",
    "loyalty.exampleCalc": "Order for {amount} PLN → you get {pts} pts → discount value: {value} PLN. So 100 pts = 5 PLN discount!",
    "loyalty.reviewBonus": "Add a review and earn 100 points (= 5 PLN discount)!",
    "account.tab.loyalty": "Points",

    "ui.changeTheme": "Change theme",
    "ui.hideGhost": "Hide ghost",
    "ui.unlockGhost": "Unlock ghost",
    "ui.lockGhost": "Lock ghost in corner",
    "config.qtyPlaceholder": "e.g. 250",
    "config.customOrderNote": "For very large or non-standard orders we prepare individual quotes. Please contact us by email",
    "config.customOrderNoteOr": "or",
    "config.minReached": "Minimum order reached",
    "config.canOrder": "you can order!",
    "config.minNeeded": "more needed",

    "seo.faq.title": "FAQ – Sticker Printing Questions | Stickers Tattooo",
    "seo.faq.description": "Answers to frequently asked questions about sticker printing: foils, laminates, file formats, production time, shipping. Stickers Tattooo.",
    "seo.blog.title": "Sticker Printing Blog – Guides & Tips | Stickers Tattooo",
    "seo.blog.description": "Stickers Tattooo printing blog. Guides: how to prepare files for printing, foil types, laminates, holographic stickers, die-cut and more.",
    "seo.contact.title": "Contact – Stickers Tattooo Printing | Katowice",
    "seo.contact.description": "Contact Stickers Tattooo sticker printing. Tel: +48 534 900 620, email: stickers.tattooo@wp.pl. Katowice, ul. Głogowa 39. Learn about our company!",
    "seo.about.title": "About Us – Stickers Tattooo Premium Printing | Katowice",
    "seo.about.description": "Meet Stickers Tattooo – premium sticker printer from Katowice. We print vinyl, holographic and die-cut stickers since 2020.",
    "seo.foils.title": "Sticker Foils – 15 Self-Adhesive Films | Stickers Tattooo",
    "seo.foils.description": "Comparison of 15 types of sticker foils: white, transparent, holographic, glitter, mirror chrome, reflective, magnetic, glow-in-the-dark.",
    "seo.breadcrumb.home": "Home",
  },

  fr: {
    "nav.configurator": "Créateur d'autocollants",
    "nav.quickPayment": "Paiement rapide",
    "nav.blog": "Blog",
    "nav.foils": "Films et stratifiés",
    "nav.faq": "FAQ",
    "nav.contact": "Contact / À propos",
    "nav.about": "À propos",
    "nav.portfolio": "Portfolio",
    "nav.cart": "Panier",
    "nav.loggedIn": "Connecté",

    "hero.badge": "Autocollants Premium",
    "hero.followUs": "Cliquez pour nous suivre →",
    "hero.description": "Autocollants premium de la plus haute qualité – holographiques, pailletés, transparents, opalescents et phosphorescents et bien d'autres. Imprimerie en ligne. Configurez et commandez en une minute.",
    "hero.cta": "Créer des autocollants",
    "welcome.title": "−10% de remise de bienvenue",
    "welcome.description": "Remise unique pour les nouveaux clients sur la première commande jusqu'à ≈ {eur} €",

    "config.title": "Paramètres des",
    "config.titleHighlight": "autocollants",
    "config.shape": "Forme",
    "config.foil": "Type de film",
    "config.foilShort": "Type de film – base sur laquelle les autocollants sont imprimés",
    "config.laminate": "Type de laminé",
    "config.laminateShort": "Type de laminé – film recouvrant les autocollants",
    "config.dimensions": "Dimensions",
    "config.width": "Largeur",
    "config.height": "Hauteur",
    "config.unit.mm": "millimètres",
    "config.unit.cm": "centimètres",
    "config.quantity": "Quantité",
    "config.file": "Fichier projet",
    "config.filePrompt": "Cliquez pour télécharger des fichiers (PDF, PNG, TIFF, JPG, BMP)",
    "config.fileSelected": "Sélectionné :",
    "config.fileOver10": "Fichiers de plus de 20 Mo, veuillez les envoyer directement à",
    "config.orVia": "ou via",
    "config.calculate": "Calculer le prix",
    "config.summary": "Résumé de la commande",
    "config.price": "Prix :",
    "config.discount": "✓ Remise quantitative appliquée",
    "config.warning": "⚠ Veuillez faire des achats réfléchis.",
    "config.warningText": "Si vous n'êtes pas sûr du rendu des autocollants — surtout ceux imprimés sur film transparent, ou les autocollants sans sous-couche blanche sur films holographiques et métallisés — consultez la section",
    "config.warningFaq": "questions fréquentes",
    "config.warningBlogPrefix": "les articles sur notre",
    "config.warningBlog": "blog",
    "config.warningGhost": "demandez au fantôme",
    "config.warningOr": "ou contactez-nous avec une question précise.",
    "config.warningText2": "Les autocollants commandés via le créateur sont imprimés exactement comme vous les avez conçus. Si après réception vous constatez que l'apparence ne vous convient pas en raison d'un manque d'information préalable, les réclamations ne seront pas acceptées.",
    
    "config.addToCart": "Ajouter au panier",
    "config.uploaded": "téléchargé...",
    "config.remove": "supprimer",
    "config.pcs": "pcs",
    "config.pieces": "pièces",
    "config.shipDate": "Expédition avant le",
    "config.files": "Fichiers",
    "config.minOrder": "Commande min. pour ce film :",
    "config.minOrderMissing": "manque",
    "config.whitePrintLabel": "Avec sous-impression blanche",
    "config.whitePrintSuffix": " – avec sous-impression blanche",
    "config.whitePrintDropdown": " · ⬤ impression blanche",

    "gallery.title": "Nos",
    "gallery.titleHighlight": "réalisations",
    "gallery.description": "Découvrez les autocollants que nous avons créés pour nos clients",
    "gallery.preparing": "Galerie en préparation",
    "gallery.soon": "Photos et vidéos bientôt disponibles",

    "faq.title": "Questions",
    "faq.titleHighlight": "fréquentes",
    "faq.showAll": "Voir toutes les questions",

    "contact.title": "Écrivez",
    "contact.titleHighlight": "-nous",
    "contact.description": "Nous savons tout sur les autocollants ! Écrivez-nous et nous répondrons à toutes vos questions.",
    "contact.name": "Prénom",
    "contact.surname": "Nom",
    "contact.email": "Email",
    "contact.phone": "Numéro de téléphone",
    "contact.message": "Message",
    "contact.send": "Envoyer",
    "contact.success": "Message envoyé ! Nous répondrons dès que possible.",

    "footer.contact": "Contact",
    "footer.info": "Informations",
    "footer.stickersCut": "Stickers Cut – notre générateur gratuit de contours de découpe",
    "footer.faq": "FAQ",
    "footer.terms": "Conditions générales",
    "footer.privacy": "Politique de confidentialité",
    "footer.cookies": "Politique des cookies",
    "footer.withdrawal": "Droit de rétractation",
    "footer.rights": "Tous droits réservés.",

    "cookie.message": "Nous utilisons des cookies uniquement à des fins techniques (session, panier, préférences). Nous n'utilisons pas de cookies marketing ou analytiques.",
    "cookie.learnMore": "En savoir plus sur notre politique de cookies",
    "cookie.accept": "Compris",

    "blog.title": "Guide des",
    "blog.titleHighlight": "autocollants",
    "blog.description": "Articles d'experts sur l'impression d'autocollants, les films, les laminés et la préparation des fichiers",
    "blog.readMore": "Lire la suite",
    "blog.comingSoon": "Articles à venir",
    "blog.allArticles": "Tous les articles",
    "blog.notFound": "Article non trouvé",
    "blog.backToBlog": "← Retour au blog",

    "qp.title": "Paiement",
    "qp.titleHighlight": "rapide",
    "qp.description": "Choisissez un mode de paiement et payez en quelques secondes",
    "qp.info": "Paiements via Przelewy24. Toutes les transactions sont cryptées et sécurisées.",
    "qp.blik": "BLIK",
    "qp.blikDesc": "Paiement rapide par code BLIK",
    "qp.transfer": "Przelewy24",
    "qp.transferDesc": "Virement en ligne via Przelewy24",
    "qp.card": "Carte bancaire",
    "qp.cardDesc": "Visa, Mastercard, autres",
    "qp.mobile": "Google Pay / Apple Pay",
    "qp.mobileDesc": "Paiement mobile",
    "qp.formSubtitle": "Remplissez le formulaire, joignez des fichiers et passez au paiement PayU.",
    "qp.contactData": "Coordonnées",
    "qp.namePlaceholder": "Nom complet *",
    "qp.emailPlaceholder": "Adresse e-mail *",
    "qp.phonePlaceholder": "Numéro de téléphone (optionnel)",
    "qp.paymentDetails": "Détails du paiement",
    "qp.amountLabel": "Montant à payer (PLN) *",
    "qp.amountPlaceholder": "ex. 150.00",
    "qp.transferTitleLabel": "Titre du virement *",
    "qp.transferTitlePlaceholder": "ex. Commande d'autocollants #123",
    "qp.attachFiles": "Joindre des fichiers (optionnel)",
    "qp.fileTypes": "PNG, JPG, PDF, SVG, TIFF, WEBP — max. 20 Mo / fichier",
    "qp.uploading": "Téléchargement...",
    "qp.clickToUpload": "Cliquez pour sélectionner des fichiers",
    "qp.orderNotes": "Notes de commande",
    "qp.notesPlaceholder": "Décrivez les détails de la commande, tailles, quantités, type de film...",
    "qp.characters": "caractères",
    "qp.verification": "Vérification",
    "qp.howMuch": "Combien font",
    "qp.enterResult": "Entrez le résultat",
    "qp.correct": "Correct !",
    "qp.incorrect": "Résultat incorrect",
    "qp.submit": "Passer la commande",
    "qp.processing": "Traitement...",
    "qp.redirectInfo": "Après avoir cliqué, vous serez redirigé vers le paiement PayU.",
    "qp.successTitle": "Commande reçue !",
    "qp.successText": "Votre commande a été reçue. Vous serez redirigé vers le paiement.",
    "qp.errorBot": "Activité suspecte détectée.",
    "qp.errorTooFast": "Formulaire rempli trop rapidement.",
    "qp.errorCaptcha": "Réponse de vérification incorrecte.",
    "qp.errorAmount": "Entrez un montant valide.",
    "qp.errorGeneral": "Une erreur est survenue lors de la création du paiement. Réessayez.",
    "qp.orderPlaced": "Commande passée !",

    "about.title": "À",
    "about.titleHighlight": "propos",
    "about.p1": "est une marque appartenant à DRUKEXPERT.PL Sp. z o.o. basée à Katowice, Pologne. Nous sommes spécialisés dans l'impression d'autocollants de la plus haute qualité sur des films spéciaux – holographiques, pailletés, miroir, transparents et bien d'autres.",
    "about.p2": "Notre imprimerie utilise les technologies d'impression numérique les plus modernes, garantissant une excellente qualité des couleurs et la durabilité des autocollants.",
    "about.p3": "Nous proposons des autocollants kiss-cut et die-cut. Un large choix de films et de laminés vous permet de créer des autocollants parfaits pour toute utilisation.",
    "about.stickersCut": "Stickers Cut est notre outil en ligne gratuit de génération de contours de découpe – conçu et développé par Stickers Tattooo. Disponible sur stickers-cut.com : importez votre visuel et l'outil prépare une ligne de découpe prête en SVG ou PDF, utilisable directement dans notre configurateur d'autocollants.",
    "about.companyData": "Données de l'entreprise",
    "about.contact": "Contact",
    "about.instagramChat": "Chat sur Instagram",

    "cart.title": "Votre",
    "cart.titleHighlight": "Panier",
    "cart.empty": "Le panier est vide",
    "cart.goToCreator": "Créateur d'autocollants",
    "cart.total": "Total :",
    "cart.back": "Retour",
    "cart.continueShopping": "Continuer les achats",
    "cart.goToPayment": "Passer au paiement",
    "cart.clearCart": "Vider le panier",
    "cart.cartCleared": "Panier vidé",
    "cart.discount_after_checkout": "La remise de bienvenue sera appliquée après avoir cliqué sur « Passer au paiement ».",
    "cart.shippingMethod": "Mode de livraison",
    "cart.loading": "Chargement...",
    "cart.products": "Produits",
    "cart.shipping": "Livraison",
    "cart.gross": "TTC",
    "shipping.paczkomat_inpost": "Point relais InPost - livraison uniquement en Pologne",
    "shipping.kurier_inpost": "Coursier InPost - livraison uniquement en Pologne",
    "shipping.kurier_europa": "Coursier Europe DPD/DHL/FedEx",
    "shipping.paczkomat_europa": "Point relais InPost Europe",
    "shipping.odbior_osobisty": "Retrait sur place",
    "shipping.uk_fedex": "Coursier UK et pays hors UE",
    "shipping.kurier_irlandia": "Coursier Irlande - FedEx / DPD",
    "shipping.kurier_hiszpania": "Coursier Espagne FedEx",
    "shipping.kurier_grecja": "Coursier Grèce",
    "shipping.locker_title": "Point relais",
    "shipping.locker_hint_pl": "Entrez le numéro du point relais InPost en Pologne (ex. KRA010, WAW123M).",
    "shipping.locker_hint_eu": "Entrez le numéro du point relais InPost dans votre pays européen (ex. AT01A, DE123456).",
    "shipping.locker_placeholder_pl": "Numéro point relais PL (ex. KRA010)",
    "shipping.locker_placeholder_eu": "Numéro point relais Europe (ex. AT01A)",
    "shipping.enter_locker": "Veuillez entrer un numéro de point relais !",

    // Account
    "account.title": "Mon compte",
    "account.logout": "Déconnexion",
    "account.tab.profile": "Profil",
    "account.tab.orders": "Commandes",
    "account.tab.saved": "Enregistrés",
    "account.name": "Nom complet",
    "account.phone": "Téléphone",
    "account.address": "Adresse de livraison",
    "account.city": "Ville",
    "account.zip": "Code postal",
    "account.locker": "Numéro du point relais",
    "account.locker_hint": "Enregistrez votre point relais préféré pour accélérer les commandes",
    "account.save": "Enregistrer",
    "account.saving": "Enregistrement...",
    "account.saved": "Données enregistrées !",
    "account.save_error": "Erreur d'enregistrement",
    "account.loading": "Chargement...",
    "account.no_orders": "Aucune commande",
    "account.order": "Commande",
    "account.date": "Date",
    "account.status": "Statut",
    "account.products": "Produits",
    "account.quantity": "Quantité",
    "account.dimensions": "Dimensions",
    "account.foil": "Film",
    "account.laminate": "Laminé",
    "account.total": "Total",
    "account.download_pdf": "Télécharger PDF",
    "account.product_n": "Produit",
    "account.no_configs": "Aucune configuration enregistrée",
    "account.no_configs_hint": "Enregistrez une configuration dans le créateur d'autocollants pour la voir ici",
    "account.use": "Utiliser",
    "account.pcs": "pcs",
    "account.delete_error": "Erreur de suppression",
    "account.config_deleted": "Configuration supprimée",
    "account.delete_account": "Supprimer le compte et les données",
    "account.delete_title": "Suppression du compte",
    "account.delete_warning": "Cette action est irréversible. Les éléments suivants seront supprimés :",
    "account.delete_item1": "Votre profil et vos données personnelles",
    "account.delete_item2": "Les configurations d'autocollants enregistrées",
    "account.delete_item3": "Le compte de connexion",
    "account.delete_orders_note": "L'historique des commandes sera conservé à des fins comptables.",
    "account.cancel": "Annuler",
    "account.confirm_delete": "Oui, supprimer le compte",
    "account.deleting": "Suppression...",
    "account.deleted": "Le compte a été supprimé",
    "account.delete_failed": "Échec de la suppression du compte",
    "account.gdpr_note": "Conformément au RGPD, vous avez le droit de supprimer vos données personnelles",
    "loyalty.title": "Programme de fidélité",
    "loyalty.subtitle": "Gagnez des points à chaque commande",
    "loyalty.points": "points",
    "loyalty.worth": "Valeur de réduction :",
    "loyalty.minRedeem": "Minimum pour échanger",
    "loyalty.earnInfo": "Pour chaque {rate} PLN dépensé, vous gagnez 1 point. Échangez vos points contre une réduction !",
    "loyalty.history": "Historique des points",
    "loyalty.howItWorks": "Comment ça marche ?",
    "loyalty.rule1": "Pour chaque {rate} PLN dépensé, vous recevez 1 point.",
    "loyalty.rule2": "Chaque point vaut {value} PLN de réduction.",
    "loyalty.rule3": "Vous pouvez échanger vos points à partir de {min} pts minimum.",
    "loyalty.example": "📌 Exemple :",
    "loyalty.exampleCalc": "Commande de {amount} PLN → vous obtenez {pts} pts → réduction : {value} PLN. Soit 100 pts = 5 PLN de réduction !",
    "loyalty.reviewBonus": "Ajoutez un avis et gagnez 100 points (= 5 PLN de réduction) !",
    "account.tab.loyalty": "Points",
    "account.status.new": "Nouvelle",
    "account.status.unpaid": "Modifications / notes",
    "account.status.processing": "En cours",
    "account.status.shipped": "Expédiée",
    "account.status.completed": "Terminée",
    "account.status.cancelled": "Annulée",
    "account.pay_now": "Finaliser le paiement",
    "account.delete_order": "Supprimer la commande",

    // Auth
    "auth.login": "Se connecter",
    "auth.register": "Créer un compte",
    "auth.reset": "Réinitialiser le mot de passe",
    "auth.login_desc": "Connectez-vous pour gérer vos commandes",
    "auth.register_desc": "Créez un compte et profitez de réductions",
    "auth.reset_desc": "Nous vous enverrons un lien de réinitialisation",
    "auth.google": "Continuer avec Google",
    "auth.or_email": "ou par email",
    "auth.name_placeholder": "Nom complet",
    "auth.password_placeholder": "Mot de passe (min. 6 caractères)",
    "auth.loading": "Chargement...",
    "auth.submit_login": "Se connecter",
    "auth.submit_register": "S'inscrire",
    "auth.submit_reset": "Envoyer le lien",
    "auth.forgot": "Mot de passe oublié ?",
    "auth.no_account": "Pas encore de compte ?",
    "auth.has_account": "Vous avez déjà un compte ?",
    "auth.back_login": "← Retour à la connexion",
    "auth.reset_sent": "Le lien de réinitialisation a été envoyé à votre email !",
    "auth.error": "Erreur",
    "auth.register_error": "Erreur d'inscription",
    "auth.login_error": "Erreur de connexion",
    "auth.google_error": "Erreur de connexion Google",
    "auth.register_success": "Compte créé ! Vérifiez votre email pour confirmer l'inscription.",
    "auth.login_success": "Connecté !",
    "auth.new_password_title": "Nouveau mot de passe",
    "auth.new_password_placeholder": "Nouveau mot de passe (min. 6 caractères)",
    "auth.new_password_saving": "Enregistrement...",
    "auth.new_password_submit": "Changer le mot de passe",
    "auth.new_password_error": "Erreur : ",
    "auth.new_password_success": "Mot de passe modifié ! Vous pouvez vous connecter.",
    "auth.new_password_label": "Nouveau mot de passe",
    "auth.confirm_password_label": "Confirmer le nouveau mot de passe",
    "auth.confirm_password_placeholder": "Saisissez à nouveau le mot de passe",
    "auth.password_too_weak": "Le mot de passe est trop faible – il doit contenir au moins 6 caractères",
    "auth.passwords_mismatch": "Les mots de passe ne correspondent pas",

    "checkout.title": "Commande",
    "checkout.personalData": "Données personnelles",
    "checkout.firstName": "Prénom",
    "checkout.lastName": "Nom",
    "checkout.email": "E-mail",
    "checkout.phone": "Téléphone",
    "checkout.deliveryAddress": "Adresse de livraison",
    "checkout.street": "Rue et numéro",
    "checkout.zip": "Code postal",
    "checkout.city": "Ville",
    "checkout.wantInvoice": "Je veux une facture TVA",
    "checkout.nip": "Numéro fiscal",
    "checkout.paymentMethod": "Mode de paiement",
    "checkout.products": "Produits :",
    "checkout.orderAndPay": "Commander et payer",
    "checkout.emptyCart": "Le panier est vide !",
    "checkout.orderSuccess": "Commande passée ! Redirection vers le paiement...",
    "checkout.totalToPay": "Total à payer",
    "checkout.orderNotes": "Notes de commande",
    "checkout.orderNotesPlaceholder": "Saisissez des notes importantes, par ex. instructions spéciales pour le projet, informations supplémentaires...",
    "checkout.characters": "caractères",
    "checkout.creditCard": "Carte bancaire",
    "checkout.saveToProfile": "Mémoriser les données pour les commandes futures",
    "checkout.welcomeDiscount": "Remise de bienvenue",
    "checkout.loyaltyUse": "Utiliser les points de fidélité",
    "checkout.loyaltyPts": "pts",
    "checkout.loyaltyDiscount": "Remise",
    "checkout.loyaltyMax50": "Max 50% de la valeur de la commande",
    "checkout.processing": "Traitement...",
    "checkout.companyDataTitle": "Données de l'entreprise du registre GUS",
    "checkout.companyNameLabel": "Nom de l'entreprise",
    "checkout.loginRequired": "Vous devez être connecté pour passer une commande",
    "checkout.sessionExpired": "Votre session a expiré. Veuillez vous reconnecter pour finaliser votre commande.",
    "cart.printOffer": "Enregistrer l'offre en PDF",

    "questions.text": "Des questions sur le fonctionnement du créateur d'autocollants ?",
    "questions.link": "Écrivez-nous",

    "tool.badge": "✂️ Outil en ligne",
    "tool.title": "Outil de contour gratuit",
    "tool.description": "Créez un contour vectoriel pour votre autocollant — gratuitement !",

    "config.whiteHint": "Comment préparer un fichier avec sous-impression blanche ?",
    "config.whiteHintLink": "Voir les instructions dans la FAQ →",
    "config.whiteRecommend": "Pour certains projets, nous recommandons d'activer la sous-impression blanche — les couleurs seront plus vives et lisibles sur un fond transparent.",

    "contact.error": "Échec de l'envoi du message. Veuillez réessayer.",

    "shape.kiss-cut": "Autocollants kiss-cut (sur feuilles)",
    "shape.kiss-cut.desc": "Autocollants découpés sur feuille, faciles à décoller",
    "shape.die-cut": "Autocollants die-cut",
    "shape.die-cut.desc": "Autocollants découpés exactement selon la forme du design",
    "foil.biala": "Film blanc",
    "foil.biala-mocny": "Film blanc adhésif fort",
    "foil.przezroczysta": "Transparent",
    "foil.przezroczysta-bialy": "Transparent – avec sous-impression blanche",
    "foil.ultra-przejrzysta": "Ultra transparent / haute brillance",
    "foil.ultra-przejrzysta-bialy": "Ultra transparent / haute brillance – avec sous-impression blanche",
    "foil.holograficzna": "Holographique",
    "foil.holograficzna-bialy": "Holographique – avec sous-impression blanche",
    "foil.holo-opalizowane": "Holo opalescent phosphorescent",
    "foil.holo-opalizowane-bialy": "Holo opalescent phosphorescent avec sous-impression blanche",
    "foil.mini-brokat": "Mini paillettes holo",
    "foil.mini-brokat-bialy": "Mini paillettes holo – avec sous-impression blanche",
    "foil.brokat-duzy": "Grandes paillettes holo",
    "foil.brokat-duzy-bialy": "Grandes paillettes holo – avec sous-impression blanche",
    "foil.szklo-tluczone": "Verre brisé holo",
    "foil.szklo-tluczone-bialy": "Verre brisé holo – avec sous-impression blanche",
    "foil.lustrzana": "Miroir argent (chrome)",
    "foil.lustrzana-bialy": "Miroir argent (chrome) – avec sous-impression blanche",
    "foil.odblaskowe": "Film réfléchissant / rétroréfléchissant",
    "foil.szczotkowane-alu": "Aluminium brossé",
    "foil.szczotkowane-alu-bialy": "Aluminium brossé – avec sous-impression blanche",
    "foil.plotno-canvas": "Autocollants canvas",
    "foil.magnetyczne": "Autocollants magnétiques",
    "foil.zielonkawa-glow": "Film vert phosphorescent",
    "foil.zielonkawa-glow-bialy": "Film vert phosphorescent – avec sous-impression blanche",
    "lam.polysk": "Laminé brillant",
    "lam.ultra-polysk": "Laminé ultra transparent / haute brillance",
    "lam.ultra-polysk-brokat": "Laminé ultra transparent / haute brillance avec Paillettes",
    "lam.mat": "Laminé mat",
    "lam.brokat-mat": "Laminé paillettes mat",
    "lam.krzyzyk-plotno": "Laminé croisé canvas",
    "lam.holo": "Laminé holo",
    "lam.holo-brokat": "Laminé holo paillettes",
    "lam.holo-gwiazdki": "Laminé holo étoiles",
    "lam.holo-tluczone": "Laminé holo verre brisé",
    "lam.holo-piksele": "Laminé holo pixels",
    "lam.skora": "Laminé texture cuir",
    "lam.podlogowy": "Laminé sol / mat",
    "lam.holo-piasek-mini": "Laminé holo sable avec mini étoiles",
    "lam.holo-duze-gwiazdki": "Laminé holo grandes étoiles",
    "lam.holo-duze-piksele": "Laminé holo grands pixels",
    "lam.nielam-polysk": "Impression vinyle haute brillance – stickers non laminés",
    "lam.nielam-mat": "Impression vinyle mat – stickers non laminés",
    "lam.bez-lam": "Sans lamination",
    "lam.ultra-polysk-sztywny": "Laminé ultra transparent / haute brillance – avec film protecteur, laminé rigide",
    "lam.ultra-polysk-brokat-sztywny": "Laminé ultra transparent / haute brillance avec Paillettes – avec film protecteur, laminé rigide",
    "lam.flatNote": "pour surfaces planes",

    "faq.q1": "Je ne suis pas sûr que mon fichier convienne à l'impression ?",
    "faq.a1": "Envoyez-nous votre fichier et notre équipe le vérifiera gratuitement et vous informera de toute correction nécessaire. Nous acceptons les fichiers PDF, AI, EPS, SVG et PNG/JPG en haute résolution (min. 300 DPI ; nous recommandons 600 DPI).",
    "faq.q2": "Autocollants sur film transparent – sans et avec sous-impression blanche ?",
    "faq.a2": "Les autocollants sans sous-impression blanche seront semi-transparents – les couleurs seront visibles mais le fond sera visible. Les autocollants avec sous-impression blanche ont une couche d'encre blanche sous les couleurs qui améliore la lisibilité des couleurs, mais en imprimerie solvant elle reste semi-transparente, donc le fond peut légèrement transparaître sur film transparent.",
    "faq.q3": "Comment préparer un fichier pour l'impression ?",
    "faq.a3": "Le fichier doit être au format vectoriel (PDF, AI, EPS, SVG) ou raster à min. 300 DPI (nous recommandons 600 DPI). Ajoutez 2mm de fond perdu. Couleurs en CMJN. La ligne de coupe doit être sur un calque séparé.",
    "faq.q4": "Confirmation des fichiers avant impression",
    "faq.a4": "Avant l'impression, nous envoyons un aperçu pour approbation. L'impression ne commence qu'après votre confirmation.",
    "faq.q5": "Comment passer une commande ?",
    "faq.a5": "Configurez vos autocollants dans le créateur – choisissez la forme, le type de film, le laminé, les dimensions et la quantité. Téléchargez votre fichier, ajoutez au panier et passez à la commande. Après paiement, nous préparerons vos autocollants.",
    "faq.q6": "Quelle est la différence entre les autocollants die-cut et kiss-cut ?",
    "faq.a6": "Die-cut – autocollant découpé exactement selon la forme du design, sans fond supplémentaire. Kiss-cut – autocollant découpé sur une feuille, facile à décoller, avec un fond rectangulaire autour du design.",
    "faq.q7": "Proposez-vous des autocollants transparents, holographiques ou métallisés avec sous-impression blanche ?",
    "faq.a7": "Oui ! Nous offrons un large choix de films spéciaux avec option de sous-impression blanche : transparents, holographiques, pailletés, miroir (chrome), aluminium brossé et bien d'autres. La sous-impression blanche améliore la visibilité des couleurs, mais en imprimerie solvant elle n'assure pas une couverture totale – sur films transparents le fond peut transparaître.",
    "faq.q8": "Comment préparer la sous-impression blanche ?",
    "faq.a8": "La sous-impression blanche doit être préparée comme un calque séparé dans le fichier. Le calque doit être nommé 'White' ou 'Sous-impression blanche'. Marquez tous les éléments avec la couleur 100% Spot White.",
    "faq.q9": "Ma commande n'a pas été imprimée correctement, que faire ?",
    "faq.a9": "Contactez-nous immédiatement en envoyant des photos des autocollants défectueux à notre email. Nous examinerons la réclamation et proposerons une solution.",
    "faq.q10": "Combien de temps prendra le traitement d'une réclamation ?",
    "faq.a10": "Nous traitons les réclamations dans les 3 à 5 jours ouvrables après réception du signalement avec photos.",
    "faq.q11": "Quand recevrai-je ma commande ?",
    "faq.a11": "Le délai standard est de 3 à 5 jours ouvrables après approbation du fichier et paiement. La livraison par DPD ou InPost prend 1 à 2 jours ouvrables supplémentaires.",
    "faq.q12": "Quelles sont les coordonnées de l'entreprise ?",
    "faq.a12": "STICKERS TATTOOO / DRUKEXPERT.PL Sp. z o.o., ul. Głogowa 39, 40-750 Katowice. Numéro fiscal et autres informations disponibles sur la facture.",
    "faq.q13": "Émettez-vous des factures TVA ?",
    "faq.a13": "Oui, nous émettons des factures TVA. Sélectionnez l'option 'Facture TVA' lors de la commande et fournissez vos coordonnées de facturation.",
    "faq.q14": "Commande mal calculée nécessitant un paiement supplémentaire.",
    "faq.a14": "Si après vérification du fichier les dimensions des autocollants diffèrent de celles de la commande, nous vous contacterons concernant tout paiement supplémentaire ou correction.",

    "calc.badge": "Calculateur",
    "calc.title": "Vérifiez",
    "calc.titleHighlight": "le prix",
    "calc.subtitle": "Déplacez les curseurs et voyez le prix des autocollants en temps réel",
    "calc.discount": "Remise quantitative",
    "calc.unitPrice": "Prix unitaire",
    "calc.totalPrice": "Prix total",
    "calc.note": "Prix indicatif pour Film Blanc die-cut avec laminé. Prix exact dans le configurateur.",
    "calc.noteGeneric": "Prix indicatif pour le film et le laminé sélectionnés. Prix exact dans le configurateur.",
    "calc.pcs": "pcs",

    "insp.badge": "Portfolio",
    "insp.title": "Nos",
    "insp.titleHighlight": "réalisations",
    "insp.subtitle": "Découvrez nos autocollants dans différents secteurs",
    "insp.industry.all": "Tous",
    "insp.industry.gastronomia": "Gastronomie",
    "insp.industry.kosmetyki": "Cosmétiques",
    "insp.industry.IT": "IT / Tech",
    "insp.industry.moda": "Mode",
    "insp.industry.motoryzacja": "Automobile",
    "insp.industry.inne": "Autres",

    "review.badge": "Avis",
    "review.title": "Ce que disent",
    "review.titleHighlight": "nos clients",
    "review.reviewsCount": "avis",
    "review.noReviews": "Soyez le premier à laisser un avis !",
    "review.addReview": "Ajouter un avis",
    "review.addReviewTitle": "Ajoutez votre avis",
    "review.yourName": "Votre prénom",
    "review.namePlaceholder": "Jean D.",
    "review.yourRating": "Note",
    "review.yourReview": "Votre avis",
    "review.reviewPlaceholder": "Partagez votre expérience avec nos autocollants...",
    "review.send": "Envoyer l'avis",
    "review.sending": "Envoi...",
    "review.fillAll": "Veuillez remplir votre nom et l'avis",
    "review.loginRequired": "Connectez-vous pour ajouter un avis",
    "review.submitted": "Merci ! Votre avis sera publié après vérification.",
    "review.alreadySubmitted": "Vous ne pouvez soumettre qu'un seul avis.",
    "review.moderationNote": "Votre avis sera publié après vérification par notre équipe",

    "ui.changeTheme": "Changer de thème",
    "ui.hideGhost": "Masquer le fantôme",
    "ui.unlockGhost": "Débloquer le fantôme",
    "ui.lockGhost": "Bloquer le fantôme dans le coin",
    "config.qtyPlaceholder": "ex. 250",
    "config.customOrderNote": "Pour les commandes très importantes ou non standard, nous préparons un devis individuel. Contactez-nous par email",
    "config.customOrderNoteOr": "ou",
    "config.minReached": "Commande minimale atteinte",
    "config.canOrder": "vous pouvez commander !",
    "config.minNeeded": "manque",

    "seo.faq.title": "FAQ – Questions fréquentes | Imprimerie Stickers Tattooo",
    "seo.faq.description": "Réponses aux questions fréquentes sur l'impression d'autocollants : films, laminés, formats, délais, expédition. Stickers Tattooo.",
    "seo.blog.title": "Blog impression d'autocollants – Guides | Stickers Tattooo",
    "seo.blog.description": "Blog de l'imprimerie Stickers Tattooo. Guides : préparation de fichiers, types de films, laminés, autocollants holographiques et plus.",
    "seo.contact.title": "Contact & À propos – Imprimerie Stickers Tattooo | Katowice",
    "seo.contact.description": "Contactez l'imprimerie Stickers Tattooo. Tél : +48 534 900 620, email : stickers.tattooo@wp.pl. Katowice, ul. Głogowa 39.",
    "seo.about.title": "À propos – Imprimerie Stickers Tattooo | Katowice",
    "seo.about.description": "Découvrez Stickers Tattooo – imprimerie d'autocollants premium de Katowice. Autocollants vinyle, holographiques et die-cut depuis 2020.",
    "seo.foils.title": "15 films pour autocollants – Comparatif | Stickers Tattooo",
    "seo.foils.description": "Comparaison de 15 types de films : blanc, transparent, holographique, paillettes, chrome miroir, réfléchissant, magnétique, phosphorescent.",
    "seo.breadcrumb.home": "Accueil",
  },

  de: {
    "nav.configurator": "Sticker-Konfigurator",
    "nav.quickPayment": "Schnelle Zahlung",
    "nav.blog": "Blog",
    "nav.foils": "Folien & Laminate",
    "nav.faq": "FAQ",
    "nav.contact": "Kontakt / Über uns",
    "nav.about": "Über uns",
    "nav.portfolio": "Portfolio",
    "nav.cart": "Warenkorb",
    "nav.loggedIn": "Eingeloggt",

    "hero.badge": "Premium Aufkleber",
    "hero.followUs": "Klicken Sie um uns zu folgen →",
    "hero.description": "Premium-Aufkleber höchster Qualität – holografisch, Glitzer, transparent, opaleszierend und nachtleuchtend und viele mehr. Online-Druckerei. Konfigurieren und bestellen Sie in einer Minute.",
    "hero.cta": "Aufkleber erstellen",
    "welcome.title": "−10% Willkommensrabatt",
    "welcome.description": "Einmaliger Rabatt für Neukunden bei der ersten Bestellung bis ≈ {eur} €",

    "config.title": "Aufkleber-",
    "config.titleHighlight": "Parameter",
    "config.shape": "Form",
    "config.foil": "Folienart",
    "config.foilShort": "Folienart – Unterlage, auf der die Aufkleber gedruckt werden",
    "config.laminate": "Laminatart",
    "config.laminateShort": "Laminatart – Folie, die die Aufkleber bedeckt",
    "config.dimensions": "Abmessungen",
    "config.width": "Breite",
    "config.height": "Höhe",
    "config.unit.mm": "Millimeter",
    "config.unit.cm": "Zentimeter",
    "config.quantity": "Menge",
    "config.file": "Projektdatei",
    "config.filePrompt": "Klicken Sie zum Hochladen (PDF, PNG, TIFF, JPG, BMP)",
    "config.fileSelected": "Ausgewählt:",
    "config.fileOver10": "Dateien über 20 MB senden Sie bitte direkt an",
    "config.orVia": "oder über",
    "config.calculate": "Preis berechnen",
    "config.summary": "Bestellübersicht",
    "config.price": "Preis:",
    "config.discount": "✓ Mengenrabatt angewendet",
    "config.warning": "⚠ Bitte überlegte Einkäufe tätigen.",
    "config.warningText": "Wenn Sie unsicher sind, wie Aufkleber aussehen werden — insbesondere auf transparenter Folie oder ohne weißen Unterdruck auf holographischen und metallischen Folien — lesen Sie den Bereich",
    "config.warningFaq": "häufig gestellte Fragen",
    "config.warningBlogPrefix": "Beiträge in unserem",
    "config.warningBlog": "Blog",
    "config.warningGhost": "fragen Sie den Geist",
    "config.warningOr": "oder kontaktieren Sie uns mit einer konkreten Frage.",
    "config.warningText2": "Im Kreator bestellte Aufkleber werden genau so gedruckt, wie Sie sie gestaltet haben. Wenn Sie nach Erhalt feststellen, dass das Aussehen nicht Ihren Erwartungen entspricht und dies auf fehlende Vorabinformation zurückzuführen ist, werden Reklamationen nicht berücksichtigt.",
    
    "config.addToCart": "In den Warenkorb",
    "config.uploaded": "hochgeladen...",
    "config.remove": "entfernen",
    "config.pcs": "Stk.",
    "config.pieces": "Stück",
    "config.shipDate": "Versand bis",
    "config.files": "Dateien",
    "config.minOrder": "Mindestbestellung für diese Folie:",
    "config.minOrderMissing": "fehlt",
    "config.whitePrintLabel": "Mit weißem Unterdruck",
    "config.whitePrintSuffix": " – mit weißem Unterdruck",
    "config.whitePrintDropdown": " · ⬤ weißer Druck",

    "gallery.title": "Unsere",
    "gallery.titleHighlight": "Arbeiten",
    "gallery.description": "Sehen Sie die Aufkleber, die wir für unsere Kunden erstellt haben",
    "gallery.preparing": "Galerie in Vorbereitung",
    "gallery.soon": "Fotos und Videos kommen bald",

    "faq.title": "Häufig gestellte",
    "faq.titleHighlight": "Fragen",
    "faq.showAll": "Alle Fragen anzeigen",

    "contact.title": "Schreiben Sie",
    "contact.titleHighlight": "uns",
    "contact.description": "Wir wissen alles über Aufkleber! Schreiben Sie uns und wir beantworten alle Ihre Fragen.",
    "contact.name": "Vorname",
    "contact.surname": "Nachname",
    "contact.email": "E-Mail",
    "contact.phone": "Telefonnummer",
    "contact.message": "Nachricht",
    "contact.send": "Senden",
    "contact.success": "Nachricht gesendet! Wir antworten so schnell wie möglich.",

    "footer.contact": "Kontakt",
    "footer.info": "Informationen",
    "footer.stickersCut": "Stickers Cut – unser kostenloser Schnittkontur-Generator",
    "footer.faq": "FAQ",
    "footer.terms": "AGB",
    "footer.privacy": "Datenschutz",
    "footer.cookies": "Cookie-Richtlinie",
    "footer.withdrawal": "Widerrufsrecht",
    "footer.rights": "Alle Rechte vorbehalten.",

    "cookie.message": "Wir verwenden Cookies ausschließlich für technische Zwecke (Sitzung, Warenkorb, Einstellungen). Wir verwenden keine Marketing- oder Analyse-Cookies.",
    "cookie.learnMore": "Mehr über unsere Cookie-Richtlinie erfahren",
    "cookie.accept": "Verstanden",

    "blog.title": "Aufkleber-",
    "blog.titleHighlight": "Ratgeber",
    "blog.description": "Expertenartikel über Aufkleberdruck, Folien, Laminate und Dateivorbereitung",
    "blog.readMore": "Weiterlesen",
    "blog.comingSoon": "Artikel kommen bald",
    "blog.allArticles": "Alle Artikel",
    "blog.notFound": "Artikel nicht gefunden",
    "blog.backToBlog": "← Zurück zum Blog",

    "qp.title": "Schnelle",
    "qp.titleHighlight": "Zahlung",
    "qp.description": "Wählen Sie eine Zahlungsmethode und zahlen Sie in Sekunden",
    "qp.info": "Zahlungen über Przelewy24. Alle Transaktionen sind verschlüsselt und sicher.",
    "qp.blik": "BLIK",
    "qp.blikDesc": "Schnelle Zahlung mit BLIK-Code",
    "qp.transfer": "Przelewy24",
    "qp.transferDesc": "Online-Überweisung via Przelewy24",
    "qp.card": "Kreditkarte",
    "qp.cardDesc": "Visa, Mastercard, andere",
    "qp.mobile": "Google Pay / Apple Pay",
    "qp.mobileDesc": "Mobile Zahlung",
    "qp.formSubtitle": "Füllen Sie das Formular aus, hängen Sie Dateien an und gehen Sie zur PayU-Zahlung.",
    "qp.contactData": "Kontaktdaten",
    "qp.namePlaceholder": "Vollständiger Name *",
    "qp.emailPlaceholder": "E-Mail-Adresse *",
    "qp.phonePlaceholder": "Telefonnummer (optional)",
    "qp.paymentDetails": "Zahlungsdetails",
    "qp.amountLabel": "Zu zahlender Betrag (PLN) *",
    "qp.amountPlaceholder": "z.B. 150.00",
    "qp.transferTitleLabel": "Überweisungstitel *",
    "qp.transferTitlePlaceholder": "z.B. Aufkleber-Bestellung #123",
    "qp.attachFiles": "Dateien anhängen (optional)",
    "qp.fileTypes": "PNG, JPG, PDF, SVG, TIFF, WEBP — max. 20 MB / Datei",
    "qp.uploading": "Hochladen...",
    "qp.clickToUpload": "Klicken Sie, um Dateien auszuwählen",
    "qp.orderNotes": "Bestellhinweise",
    "qp.notesPlaceholder": "Beschreiben Sie Bestelldetails, Größen, Mengen, Folienart...",
    "qp.characters": "Zeichen",
    "qp.verification": "Verifizierung",
    "qp.howMuch": "Wie viel ist",
    "qp.enterResult": "Ergebnis eingeben",
    "qp.correct": "Richtig!",
    "qp.incorrect": "Falsches Ergebnis",
    "qp.submit": "Bestellung aufgeben",
    "qp.processing": "Verarbeitung...",
    "qp.redirectInfo": "Nach dem Klicken werden Sie zur PayU-Zahlung weitergeleitet.",
    "qp.successTitle": "Bestellung eingegangen!",
    "qp.successText": "Ihre Bestellung wurde angenommen. Sie werden in Kürze zur Zahlung weitergeleitet.",
    "qp.errorBot": "Verdächtige Aktivität erkannt.",
    "qp.errorTooFast": "Formular zu schnell ausgefüllt.",
    "qp.errorCaptcha": "Falsche Verifizierungsantwort.",
    "qp.errorAmount": "Geben Sie einen gültigen Betrag ein.",
    "qp.errorGeneral": "Beim Erstellen der Zahlung ist ein Fehler aufgetreten. Versuchen Sie es erneut.",
    "qp.orderPlaced": "Bestellung aufgegeben!",

    "about.title": "Über",
    "about.titleHighlight": "uns",
    "about.p1": "ist eine Marke von DRUKEXPERT.PL Sp. z o.o. mit Sitz in Katowice, Polen. Wir sind spezialisiert auf den Druck von Aufklebern höchster Qualität auf Spezialfolien – holografisch, Glitzer, Spiegel, transparent und viele mehr.",
    "about.p2": "Unsere Druckerei nutzt modernste Digitaldrucktechnologien und gewährleistet hervorragende Farbqualität und Aufkleberhaltbarkeit.",
    "about.p3": "Wir bieten sowohl Kiss-Cut- als auch Die-Cut-Aufkleber an. Eine große Auswahl an Folien und Laminaten ermöglicht es, perfekte Aufkleber für jede Anwendung zu erstellen.",
    "about.stickersCut": "Stickers Cut ist unser kostenloses Online-Tool zur Erstellung von Schnittkonturen – entwickelt und betreut von Stickers Tattooo. Sie finden es unter stickers-cut.com: Grafik hochladen und das Tool erzeugt eine fertige Schnittlinie als SVG oder PDF, die Sie direkt in unserem Aufkleber-Konfigurator verwenden können.",
    "about.companyData": "Firmendaten",
    "about.contact": "Kontakt",
    "about.instagramChat": "Chat auf Instagram",

    "cart.title": "Ihr",
    "cart.titleHighlight": "Warenkorb",
    "cart.empty": "Warenkorb ist leer",
    "cart.goToCreator": "Sticker-Konfigurator",
    "cart.total": "Gesamt:",
    "cart.back": "Zurück",
    "cart.continueShopping": "Weiter einkaufen",
    "cart.goToPayment": "Zur Zahlung",
    "cart.clearCart": "Warenkorb leeren",
    "cart.cartCleared": "Warenkorb geleert",
    "cart.discount_after_checkout": "Der Willkommensrabatt wird nach dem Klick auf \u201eZur Zahlung\u201c angerechnet.",
    "cart.shippingMethod": "Versandart",
    "cart.loading": "Laden...",
    "cart.products": "Produkte",
    "cart.shipping": "Versand",
    "cart.gross": "brutto",
    "shipping.paczkomat_inpost": "Paketautomat InPost - Lieferung nur innerhalb Polens",
    "shipping.kurier_inpost": "Kurier InPost - Lieferung nur innerhalb Polens",
    "shipping.kurier_europa": "Kurier Europa DPD/DHL/FedEx",
    "shipping.paczkomat_europa": "Paketautomat InPost Europa",
    "shipping.odbior_osobisty": "Selbstabholung",
    "shipping.uk_fedex": "Kurier UK und Nicht-EU-Länder",
    "shipping.kurier_irlandia": "Kurier Irland - FedEx / DPD",
    "shipping.kurier_hiszpania": "Kurier Spanien FedEx",
    "shipping.kurier_grecja": "Kurier Griechenland",
    "shipping.locker_title": "Paketautomat",
    "shipping.locker_hint_pl": "Geben Sie die Nummer des InPost-Paketautomaten in Polen ein (z.B. KRA010, WAW123M).",
    "shipping.locker_hint_eu": "Geben Sie die Nummer des InPost-Paketautomaten in Ihrem europäischen Land ein (z.B. AT01A, DE123456).",
    "shipping.locker_placeholder_pl": "Paketautomat-Nr. PL (z.B. KRA010)",
    "shipping.locker_placeholder_eu": "Paketautomat-Nr. Europa (z.B. AT01A)",
    "shipping.enter_locker": "Bitte geben Sie eine Paketautomat-Nummer ein!",

    // Account
    "account.title": "Mein Konto",
    "account.logout": "Abmelden",
    "account.tab.profile": "Profil",
    "account.tab.orders": "Bestellungen",
    "account.tab.saved": "Gespeichert",
    "account.name": "Vollständiger Name",
    "account.phone": "Telefon",
    "account.address": "Lieferadresse",
    "account.city": "Stadt",
    "account.zip": "Postleitzahl",
    "account.locker": "Paketautomat-Nummer",
    "account.locker_hint": "Speichern Sie Ihren Lieblingspaketautomaten für schnellere Bestellungen",
    "account.save": "Speichern",
    "account.saving": "Wird gespeichert...",
    "account.saved": "Daten gespeichert!",
    "account.save_error": "Speicherfehler",
    "account.loading": "Laden...",
    "account.no_orders": "Keine Bestellungen",
    "account.order": "Bestellung",
    "account.date": "Datum",
    "account.status": "Status",
    "account.products": "Produkte",
    "account.quantity": "Menge",
    "account.dimensions": "Abmessungen",
    "account.foil": "Folie",
    "account.laminate": "Laminat",
    "account.total": "Gesamt",
    "account.download_pdf": "PDF herunterladen",
    "account.product_n": "Produkt",
    "account.no_configs": "Keine gespeicherten Konfigurationen",
    "account.no_configs_hint": "Speichern Sie eine Konfiguration im Aufkleber-Ersteller, um sie hier zu sehen",
    "account.use": "Verwenden",
    "account.pcs": "Stk.",
    "account.delete_error": "Löschfehler",
    "account.config_deleted": "Konfiguration gelöscht",
    "account.delete_account": "Konto und Daten löschen",
    "account.delete_title": "Konto löschen",
    "account.delete_warning": "Diese Aktion ist unwiderruflich. Folgendes wird gelöscht:",
    "account.delete_item1": "Ihr Profil und persönliche Daten",
    "account.delete_item2": "Gespeicherte Aufkleber-Konfigurationen",
    "account.delete_item3": "Login-Konto",
    "account.delete_orders_note": "Die Bestellhistorie wird zu Buchhaltungszwecken aufbewahrt.",
    "account.cancel": "Abbrechen",
    "account.confirm_delete": "Ja, Konto löschen",
    "account.deleting": "Wird gelöscht...",
    "account.deleted": "Konto wurde gelöscht",
    "account.delete_failed": "Konto konnte nicht gelöscht werden",
    "account.gdpr_note": "Gemäß DSGVO haben Sie das Recht auf Löschung Ihrer personenbezogenen Daten",
    "account.status.new": "Neu",
    "account.status.unpaid": "Dateiänderungen / Hinweise",
    "account.status.processing": "In Bearbeitung",
    "account.status.shipped": "Versendet",
    "account.status.completed": "Abgeschlossen",
    "account.status.cancelled": "Storniert",
    "account.pay_now": "Zahlung abschließen",
    "account.delete_order": "Bestellung löschen",

    // Auth
    "auth.login": "Anmelden",
    "auth.register": "Konto erstellen",
    "auth.reset": "Passwort zurücksetzen",
    "auth.login_desc": "Melden Sie sich an, um Ihre Bestellungen zu verwalten",
    "auth.register_desc": "Erstellen Sie ein Konto und genießen Sie Rabatte",
    "auth.reset_desc": "Wir senden Ihnen einen Link zum Zurücksetzen",
    "auth.google": "Weiter mit Google",
    "auth.or_email": "oder per E-Mail",
    "auth.name_placeholder": "Vollständiger Name",
    "auth.password_placeholder": "Passwort (min. 6 Zeichen)",
    "auth.loading": "Laden...",
    "auth.submit_login": "Anmelden",
    "auth.submit_register": "Registrieren",
    "auth.submit_reset": "Link senden",
    "auth.forgot": "Passwort vergessen?",
    "auth.no_account": "Noch kein Konto?",
    "auth.has_account": "Haben Sie bereits ein Konto?",
    "auth.back_login": "← Zurück zur Anmeldung",
    "auth.reset_sent": "Der Link zum Zurücksetzen des Passworts wurde an Ihre E-Mail gesendet!",
    "auth.error": "Fehler",
    "auth.register_error": "Registrierungsfehler",
    "auth.login_error": "Anmeldefehler",
    "auth.google_error": "Google-Anmeldefehler",
    "auth.register_success": "Konto erstellt! Überprüfen Sie Ihre E-Mail, um die Registrierung zu bestätigen.",
    "auth.login_success": "Angemeldet!",
    "auth.new_password_title": "Neues Passwort",
    "auth.new_password_placeholder": "Neues Passwort (min. 6 Zeichen)",
    "auth.new_password_saving": "Speichern...",
    "auth.new_password_submit": "Passwort ändern",
    "auth.new_password_error": "Fehler: ",
    "auth.new_password_success": "Passwort geändert! Sie können sich jetzt anmelden.",
    "auth.new_password_label": "Neues Passwort",
    "auth.confirm_password_label": "Neues Passwort bestätigen",
    "auth.confirm_password_placeholder": "Passwort erneut eingeben",
    "auth.password_too_weak": "Das Passwort ist zu schwach – es muss mindestens 6 Zeichen haben",
    "auth.passwords_mismatch": "Die Passwörter stimmen nicht überein",

    "loyalty.title": "Treueprogramm",
    "loyalty.subtitle": "Sammeln Sie Punkte bei jeder Bestellung",
    "loyalty.points": "Punkte",
    "loyalty.worth": "Rabattwert:",
    "loyalty.minRedeem": "Minimum zum Einlösen",
    "loyalty.earnInfo": "Für jede ausgegebene {rate} PLN erhalten Sie 1 Punkt. Tauschen Sie Punkte gegen einen Rabatt ein!",
    "loyalty.history": "Punktehistorie",
    "loyalty.howItWorks": "Wie funktioniert es?",
    "loyalty.rule1": "Für jede ausgegebene {rate} PLN erhalten Sie 1 Punkt.",
    "loyalty.rule2": "Jeder Punkt ist {value} PLN Rabatt wert.",
    "loyalty.rule3": "Sie können Punkte ab {min} Pkt. gegen einen Rabatt einlösen.",
    "loyalty.example": "📌 Beispiel:",
    "loyalty.exampleCalc": "Bestellung für {amount} PLN → Sie erhalten {pts} Pkt. → Rabatt: {value} PLN. Also 100 Pkt. = 5 PLN Rabatt!",
    "loyalty.reviewBonus": "Bewertung abgeben und 100 Punkte (= 5 PLN Rabatt) verdienen!",
    "account.tab.loyalty": "Punkte",

    "checkout.title": "Bestellung",
    "checkout.personalData": "Persönliche Daten",
    "checkout.firstName": "Vorname",
    "checkout.lastName": "Nachname",
    "checkout.email": "E-Mail",
    "checkout.phone": "Telefon",
    "checkout.deliveryAddress": "Lieferadresse",
    "checkout.street": "Straße und Nummer",
    "checkout.zip": "Postleitzahl",
    "checkout.city": "Stadt",
    "checkout.wantInvoice": "Ich möchte eine MwSt-Rechnung",
    "checkout.nip": "Steuernummer",
    "checkout.paymentMethod": "Zahlungsmethode",
    "checkout.products": "Produkte:",
    "checkout.orderAndPay": "Bestellen und bezahlen",
    "checkout.emptyCart": "Warenkorb ist leer!",
    "checkout.orderSuccess": "Bestellung aufgegeben! Weiterleitung zur Zahlung...",
    "checkout.totalToPay": "Gesamtbetrag",
    "checkout.orderNotes": "Bestellhinweise",
    "checkout.orderNotesPlaceholder": "Geben Sie wichtige Hinweise ein, z.B. spezielle Anweisungen zum Projekt, zusätzliche Informationen...",
    "checkout.characters": "Zeichen",
    "checkout.creditCard": "Kreditkarte",
    "checkout.saveToProfile": "Daten für zukünftige Bestellungen merken",
    "checkout.welcomeDiscount": "Willkommensrabatt",
    "checkout.loyaltyUse": "Treuepunkte einlösen",
    "checkout.loyaltyPts": "Pkt",
    "checkout.loyaltyDiscount": "Rabatt",
    "checkout.loyaltyMax50": "Max 50% des Bestellwerts",
    "checkout.processing": "Verarbeitung...",
    "checkout.companyDataTitle": "Firmendaten aus dem GUS-Register",
    "checkout.companyNameLabel": "Firmenname",
    "checkout.loginRequired": "Sie müssen angemeldet sein, um eine Bestellung aufzugeben",
    "checkout.sessionExpired": "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an, um die Bestellung abzuschließen.",
    "cart.printOffer": "Angebot als PDF speichern",

    "questions.text": "Fragen zur Funktionsweise des Sticker-Konfigurators?",
    "questions.link": "Schreiben Sie uns",

    "tool.badge": "✂️ Online-Werkzeug",
    "tool.title": "Kostenloses Kontur-Werkzeug",
    "tool.description": "Erstellen Sie einen Konturvektor für Ihren Aufkleber — kostenlos!",

    "config.whiteHint": "Wie bereitet man eine Datei mit weißem Unterdruck vor?",
    "config.whiteHintLink": "Anleitung in der FAQ ansehen →",
    "config.whiteRecommend": "Für manche Designs empfehlen wir den weißen Unterdruck — die Farben werden auf transparentem Hintergrund lebendiger und besser lesbar.",

    "contact.error": "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",

    "shape.kiss-cut": "Kiss-Cut Aufkleber (auf Bögen)",
    "shape.kiss-cut.desc": "Aufkleber auf Bögen, leicht abziehbar",
    "shape.die-cut": "Die-Cut Aufkleber",
    "shape.die-cut.desc": "Aufkleber genau nach der Form des Designs geschnitten",
    "foil.biala": "Weiße Folie",
    "foil.biala-mocny": "Weiße Folie starker Kleber",
    "foil.przezroczysta": "Transparent",
    "foil.przezroczysta-bialy": "Transparent – mit weißem Unterdruck",
    "foil.ultra-przejrzysta": "Ultra klar / Hochglanz",
    "foil.ultra-przejrzysta-bialy": "Ultra klar / Hochglanz – mit weißem Unterdruck",
    "foil.holograficzna": "Holografisch",
    "foil.holograficzna-bialy": "Holografisch – mit weißem Unterdruck",
    "foil.holo-opalizowane": "Holo opaleszierend nachtleuchtend",
    "foil.holo-opalizowane-bialy": "Holo opaleszierend nachtleuchtend mit weißem Unterdruck",
    "foil.mini-brokat": "Mini Glitzer holo",
    "foil.mini-brokat-bialy": "Mini Glitzer holo – mit weißem Unterdruck",
    "foil.brokat-duzy": "Großer Glitzer holo",
    "foil.brokat-duzy-bialy": "Großer Glitzer holo – mit weißem Unterdruck",
    "foil.szklo-tluczone": "Bruchglas holo",
    "foil.szklo-tluczone-bialy": "Bruchglas holo – mit weißem Unterdruck",
    "foil.lustrzana": "Spiegel silber (Chrom)",
    "foil.lustrzana-bialy": "Spiegel silber (Chrom) – mit weißem Unterdruck",
    "foil.odblaskowe": "Reflektierende / retroreflektierende Folie",
    "foil.szczotkowane-alu": "Gebürstetes Aluminium",
    "foil.szczotkowane-alu-bialy": "Gebürstetes Aluminium – mit weißem Unterdruck",
    "foil.plotno-canvas": "Canvas Aufkleber",
    "foil.magnetyczne": "Magnetische Aufkleber",
    "foil.zielonkawa-glow": "Grüne nachtleuchtende Folie",
    "foil.zielonkawa-glow-bialy": "Grüne nachtleuchtende Folie – mit weißem Unterdruck",
    "lam.polysk": "Glanz-Laminat",
    "lam.ultra-polysk": "Ultra klares / Hochglanz-Laminat",
    "lam.ultra-polysk-brokat": "Ultra klares / Hochglanz-Laminat mit Glitzer",
    "lam.mat": "Matt-Laminat",
    "lam.brokat-mat": "Matt-Glitzer-Laminat",
    "lam.krzyzyk-plotno": "Kreuzschraffur-Canvas-Laminat",
    "lam.holo": "Holo-Laminat",
    "lam.holo-brokat": "Holo-Glitzer-Laminat",
    "lam.holo-gwiazdki": "Holo-Sterne-Laminat",
    "lam.holo-tluczone": "Holo-Bruchglas-Laminat",
    "lam.holo-piksele": "Holo-Pixel-Laminat",
    "lam.skora": "Lederstruktur-Laminat",
    "lam.podlogowy": "Boden- / Matt-Laminat",
    "lam.holo-piasek-mini": "Holo-Sand mit Mini-Sternen-Laminat",
    "lam.holo-duze-gwiazdki": "Holo-Laminat große Sterne",
    "lam.holo-duze-piksele": "Holo-Laminat große Pixel",
    "lam.nielam-polysk": "Hochglanz-Vinyldruck – unlaminierte Sticker",
    "lam.nielam-mat": "Matt-Vinyldruck – unlaminierte Sticker",
    "lam.bez-lam": "Ohne Laminierung",
    "lam.ultra-polysk-sztywny": "Ultra klares / Hochglanz-Laminat – mit Schutzfolie, starres Laminat",
    "lam.ultra-polysk-brokat-sztywny": "Ultra klares / Hochglanz-Laminat mit Glitzer – mit Schutzfolie, starres Laminat",
    "lam.flatNote": "für flache Oberflächen",

    "faq.q1": "Ich bin mir nicht sicher, ob meine Datei zum Drucken geeignet ist?",
    "faq.a1": "Senden Sie uns Ihre Datei und unser Team prüft sie kostenlos und informiert Sie über notwendige Korrekturen. Wir akzeptieren PDF, AI, EPS, SVG und hochauflösende PNG/JPG (min. 300 DPI; wir empfehlen 600 DPI).",
    "faq.q2": "Aufkleber auf transparenter Folie – ohne und mit weißem Unterdruck?",
    "faq.a2": "Aufkleber ohne weißen Unterdruck sind halbtransparent – Farben sind sichtbar, aber der Hintergrund scheint durch. Aufkleber mit weißem Unterdruck haben eine Schicht weißer Tinte unter den Farben, die die Farblesepbarkeit verbessert, aber im Lösungsmitteldruck bleibt sie halbtransparent, sodass bei transparenten Folien der Hintergrund leicht durchscheinen kann.",
    "faq.q3": "Wie bereite ich eine Datei zum Drucken vor?",
    "faq.a3": "Die Datei sollte im Vektorformat (PDF, AI, EPS, SVG) oder Raster mit min. 300 DPI sein (wir empfehlen 600 DPI). Fügen Sie 2mm Beschnitt hinzu. Farben in CMYK. Die Schnittlinie auf einer separaten Ebene.",
    "faq.q4": "Dateibestätigung vor dem Druck",
    "faq.a4": "Vor dem Druck senden wir eine Vorschau zur Genehmigung. Der Druck beginnt erst nach Ihrer Bestätigung.",
    "faq.q5": "Wie bestelle ich?",
    "faq.a5": "Konfigurieren Sie Ihre Aufkleber im Konfigurator – wählen Sie Form, Folienart, Laminat, Abmessungen und Menge. Laden Sie Ihre Datei hoch, legen Sie in den Warenkorb und schließen Sie die Bestellung ab.",
    "faq.q6": "Was ist der Unterschied zwischen Die-Cut und Kiss-Cut?",
    "faq.a6": "Die-Cut – Aufkleber genau nach der Form des Designs geschnitten, ohne Hintergrund. Kiss-Cut – Aufkleber auf einem Bogen, leicht abziehbar, mit rechteckigem Hintergrund um das Design.",
    "faq.q7": "Bieten Sie transparente, holografische oder metallische Aufkleber mit weißem Unterdruck an?",
    "faq.a7": "Ja! Wir bieten eine große Auswahl an Spezialfolien mit Option für weißen Unterdruck: transparent, holografisch, Glitzer, Spiegel (Chrom), gebürstetes Aluminium und viele mehr. Weißer Unterdruck verbessert die Farbsichtbarkeit, bietet aber im Lösungsmitteldruck keine vollständige Deckung – bei transparenten Folien kann der Hintergrund durchscheinen.",
    "faq.q8": "Wie bereite ich den weißen Unterdruck vor?",
    "faq.a8": "Der weiße Unterdruck sollte als separate Ebene in der Datei vorbereitet werden. Die Ebene sollte 'White' oder 'Weißer Unterdruck' heißen. Markieren Sie alle Elemente mit 100% Spot White.",
    "faq.q9": "Meine Bestellung wurde nicht korrekt gedruckt, was soll ich tun?",
    "faq.a9": "Kontaktieren Sie uns sofort und senden Sie Fotos der fehlerhaften Aufkleber an unsere E-Mail. Wir prüfen die Reklamation und schlagen eine Lösung vor.",
    "faq.q10": "Wie lange dauert die Bearbeitung einer Reklamation?",
    "faq.a10": "Wir bearbeiten Reklamationen innerhalb von 3–5 Werktagen nach Eingang der Meldung mit Fotos.",
    "faq.q11": "Wann erhalte ich meine Bestellung?",
    "faq.a11": "Die Standardproduktionszeit beträgt 3–5 Werktage ab Dateigenehmigung und Zahlung. Der Versand per DPD oder InPost dauert zusätzlich 1–2 Werktage.",
    "faq.q12": "Wie lauten die Firmendaten und Zahlungsinformationen?",
    "faq.a12": "STICKERS TATTOOO / DRUKEXPERT.PL Sp. z o.o., ul. Głogowa 39, 40-750 Katowice. Steuernummer und weitere Daten auf der Rechnung.",
    "faq.q13": "Stellen Sie MwSt-Rechnungen aus?",
    "faq.a13": "Ja, wir stellen MwSt-Rechnungen aus. Wählen Sie die Option 'MwSt-Rechnung' bei der Bestellung und geben Sie Ihre Rechnungsdaten an.",
    "faq.q14": "Falsch berechnete Bestellung mit Nachzahlung.",
    "faq.a14": "Wenn nach der Dateiprüfung die Aufkleberabmessungen von der Bestellung abweichen, kontaktieren wir Sie bezüglich einer Nachzahlung oder Korrektur.",

    "calc.badge": "Rechner",
    "calc.title": "Preis",
    "calc.titleHighlight": "prüfen",
    "calc.subtitle": "Bewegen Sie die Schieberegler und sehen Sie den Aufkleberpreis in Echtzeit",
    "calc.discount": "Mengenrabatt",
    "calc.unitPrice": "Stückpreis",
    "calc.totalPrice": "Gesamtpreis",
    "calc.note": "Richtpreis für Weiße Folie die-cut mit Laminat. Genauer Preis im Konfigurator.",
    "calc.noteGeneric": "Richtpreis für die gewählte Folie und das Laminat. Genauer Preis im Konfigurator.",
    "calc.pcs": "Stk.",

    "insp.badge": "Portfolio",
    "insp.title": "Unsere",
    "insp.titleHighlight": "Arbeiten",
    "insp.subtitle": "Sehen Sie, wie unsere Aufkleber in verschiedenen Branchen aussehen",
    "insp.industry.all": "Alle",
    "insp.industry.gastronomia": "Gastronomie",
    "insp.industry.kosmetyki": "Kosmetik",
    "insp.industry.IT": "IT / Tech",
    "insp.industry.moda": "Mode",
    "insp.industry.motoryzacja": "Automobil",
    "insp.industry.inne": "Sonstige",

    "review.badge": "Bewertungen",
    "review.title": "Was unsere",
    "review.titleHighlight": "Kunden sagen",
    "review.reviewsCount": "Bewertungen",
    "review.noReviews": "Seien Sie der Erste, der eine Bewertung abgibt!",
    "review.addReview": "Bewertung hinzufügen",
    "review.addReviewTitle": "Ihre Bewertung abgeben",
    "review.yourName": "Ihr Name",
    "review.namePlaceholder": "Max M.",
    "review.yourRating": "Bewertung",
    "review.yourReview": "Ihre Bewertung",
    "review.reviewPlaceholder": "Teilen Sie Ihre Erfahrung mit unseren Aufklebern...",
    "review.send": "Bewertung senden",
    "review.sending": "Wird gesendet...",
    "review.fillAll": "Bitte Name und Bewertung ausfüllen",
    "review.loginRequired": "Bitte melden Sie sich an, um eine Bewertung abzugeben",
    "review.submitted": "Vielen Dank! Ihre Bewertung wird nach Prüfung veröffentlicht.",
    "review.alreadySubmitted": "Sie können nur eine Bewertung abgeben.",
    "review.moderationNote": "Ihre Bewertung wird nach Prüfung durch unser Team veröffentlicht",

    "ui.changeTheme": "Design ändern",
    "ui.hideGhost": "Geist ausblenden",
    "ui.unlockGhost": "Geist freigeben",
    "ui.lockGhost": "Geist in Ecke fixieren",
    "config.qtyPlaceholder": "z.B. 250",
    "config.customOrderNote": "Für sehr große oder Sonderbestellungen erstellen wir individuelle Angebote. Kontaktieren Sie uns per E-Mail",
    "config.customOrderNoteOr": "oder",
    "config.minReached": "Mindestbestellwert erreicht",
    "config.canOrder": "Sie können bestellen!",
    "config.minNeeded": "fehlt",

    "seo.faq.title": "FAQ – Aufkleberdruck Fragen | Stickers Tattooo",
    "seo.faq.description": "Antworten auf häufig gestellte Fragen zum Aufkleberdruck: Folien, Laminate, Dateiformate, Produktionszeit, Versand. Stickers Tattooo.",
    "seo.blog.title": "Blog Aufkleberdruck – Ratgeber | Stickers Tattooo",
    "seo.blog.description": "Blog der Druckerei Stickers Tattooo. Ratgeber: Dateivorbereitung, Folienarten, Laminate, holografische Aufkleber und mehr.",
    "seo.contact.title": "Kontakt – Aufkleberdruck Stickers Tattooo | Katowice",
    "seo.contact.description": "Kontaktieren Sie die Druckerei Stickers Tattooo. Tel: +48 534 900 620, E-Mail: stickers.tattooo@wp.pl. Katowice, ul. Głogowa 39.",
    "seo.about.title": "Über uns – Aufkleberdruck Stickers Tattooo | Katowice",
    "seo.about.description": "Lernen Sie Stickers Tattooo kennen – Premium-Aufkleberdrucker aus Katowice. Vinyl-, Hologramm- und Die-Cut-Aufkleber seit 2020.",
    "seo.foils.title": "Folienarten – 15 selbstklebende Folien | Stickers Tattooo",
    "seo.foils.description": "Vergleich von 15 Folienarten: weiß, transparent, holografisch, Glitzer, Spiegel-Chrom, reflektierend, magnetisch, nachtleuchtend.",
    "seo.breadcrumb.home": "Startseite",
  },

  it: {
    "nav.configurator": "Creatore di adesivi",
    "nav.quickPayment": "Pagamento rapido",
    "nav.blog": "Blog",
    "nav.foils": "Pellicole e laminati",
    "nav.faq": "FAQ",
    "nav.contact": "Contatto / Chi siamo",
    "nav.about": "Chi siamo",
    "nav.portfolio": "Portfolio",
    "nav.cart": "Carrello",
    "nav.loggedIn": "Connesso",

    "hero.badge": "Adesivi Premium",
    "hero.followUs": "Clicca per seguirci →",
    "hero.description": "Adesivi premium della massima qualità – olografici, glitter, trasparenti, opalescenti e fosforescenti e molti altri. Tipografia online. Configura e ordina in un minuto.",
    "hero.cta": "Crea adesivi",
    "welcome.title": "−10% sconto di benvenuto",
    "welcome.description": "Sconto unico per nuovi clienti sul primo ordine fino a ≈ {eur} €",

    "config.title": "Parametri degli",
    "config.titleHighlight": "adesivi",
    "config.shape": "Forma",
    "config.foil": "Tipo di pellicola",
    "config.foilShort": "Tipo di pellicola – base su cui vengono stampati gli adesivi",
    "config.laminate": "Tipo di laminato",
    "config.laminateShort": "Tipo di laminato – pellicola che ricopre gli adesivi",
    "config.dimensions": "Dimensioni",
    "config.width": "Larghezza",
    "config.height": "Altezza",
    "config.unit.mm": "millimetri",
    "config.unit.cm": "centimetri",
    "config.quantity": "Quantità",
    "config.file": "File del progetto",
    "config.filePrompt": "Clicca per caricare file (PDF, PNG, TIFF, JPG, BMP)",
    "config.fileSelected": "Selezionato:",
    "config.fileOver10": "File superiori a 20 MB si prega di inviarli direttamente a",
    "config.orVia": "o tramite",
    "config.calculate": "Calcola prezzo",
    "config.summary": "Riepilogo ordine",
    "config.price": "Prezzo:",
    "config.discount": "✓ Sconto quantità applicato",
    "config.warning": "⚠ Si prega di fare acquisti consapevoli.",
    "config.warningText": "Se non sei sicuro dell'aspetto degli adesivi — soprattutto quelli stampati su pellicola trasparente, o gli adesivi senza sottofondo bianco su pellicole olografiche e metalliche — consulta la sezione",
    "config.warningFaq": "domande frequenti",
    "config.warningBlogPrefix": "gli articoli sul nostro",
    "config.warningBlog": "blog",
    "config.warningGhost": "chiedi al fantasma",
    "config.warningOr": "o contattaci con una domanda specifica.",
    "config.warningText2": "Gli adesivi ordinati tramite il creatore vengono stampati esattamente come li hai progettati. Se dopo aver ricevuto la spedizione scopri che l'aspetto non soddisfa le tue aspettative a causa della mancata consultazione delle informazioni sul prodotto, i reclami non saranno accettati.",
    
    "config.addToCart": "Aggiungi al carrello",
    "config.uploaded": "caricato...",
    "config.remove": "rimuovi",
    "config.pcs": "pz",
    "config.pieces": "pezzi",
    "config.shipDate": "Spedizione entro",
    "config.files": "File",
    "config.minOrder": "Ordine min. per questa pellicola:",
    "config.minOrderMissing": "mancano",
    "config.whitePrintLabel": "Con sottostampa bianca",
    "config.whitePrintSuffix": " – con sottostampa bianca",
    "config.whitePrintDropdown": " · ⬤ stampa bianca",

    "gallery.title": "Le nostre",
    "gallery.titleHighlight": "realizzazioni",
    "gallery.description": "Scopri gli adesivi che abbiamo creato per i nostri clienti",
    "gallery.preparing": "Galleria in preparazione",
    "gallery.soon": "Foto e video in arrivo",

    "faq.title": "Domande",
    "faq.titleHighlight": "frequenti",
    "faq.showAll": "Vedi tutte le domande",

    "contact.title": "Scrivici",
    "contact.titleHighlight": "",
    "contact.description": "Sappiamo tutto sugli adesivi! Scrivici e risponderemo a tutte le tue domande.",
    "contact.name": "Nome",
    "contact.surname": "Cognome",
    "contact.email": "Email",
    "contact.phone": "Numero di telefono",
    "contact.message": "Messaggio",
    "contact.send": "Invia",
    "contact.success": "Messaggio inviato! Risponderemo il prima possibile.",

    "footer.contact": "Contatto",
    "footer.info": "Informazioni",
    "footer.stickersCut": "Stickers Cut – il nostro generatore gratuito di contorni di taglio",
    "footer.faq": "FAQ",
    "footer.terms": "Termini e condizioni",
    "footer.privacy": "Informativa sulla privacy",
    "footer.cookies": "Politica dei cookie",
    "footer.withdrawal": "Diritto di recesso",
    "footer.rights": "Tutti i diritti riservati.",

    "cookie.message": "Utilizziamo i cookie solo per scopi tecnici (sessione, carrello, preferenze). Non utilizziamo cookie di marketing o analitici.",
    "cookie.learnMore": "Scopri di più sulla nostra politica sui cookie",
    "cookie.accept": "Ho capito",

    "blog.title": "Guida agli",
    "blog.titleHighlight": "adesivi",
    "blog.description": "Articoli di esperti sulla stampa di adesivi, pellicole, laminati e preparazione dei file",
    "blog.readMore": "Leggi di più",
    "blog.comingSoon": "Articoli in arrivo",
    "blog.allArticles": "Tutti gli articoli",
    "blog.notFound": "Articolo non trovato",
    "blog.backToBlog": "← Torna al blog",

    "qp.title": "Pagamento",
    "qp.titleHighlight": "rapido",
    "qp.description": "Scegli un metodo di pagamento e paga in pochi secondi",
    "qp.info": "Pagamenti tramite Przelewy24. Tutte le transazioni sono crittografate e sicure.",
    "qp.blik": "BLIK",
    "qp.blikDesc": "Pagamento rapido con codice BLIK",
    "qp.transfer": "Przelewy24",
    "qp.transferDesc": "Bonifico online tramite Przelewy24",
    "qp.card": "Carta di credito",
    "qp.cardDesc": "Visa, Mastercard, altre",
    "qp.mobile": "Google Pay / Apple Pay",
    "qp.mobileDesc": "Pagamento mobile",
    "qp.formSubtitle": "Compila il modulo, allega i file e procedi al pagamento PayU.",
    "qp.contactData": "Dati di contatto",
    "qp.namePlaceholder": "Nome completo *",
    "qp.emailPlaceholder": "Indirizzo e-mail *",
    "qp.phonePlaceholder": "Numero di telefono (opzionale)",
    "qp.paymentDetails": "Dettagli del pagamento",
    "qp.amountLabel": "Importo da pagare (PLN) *",
    "qp.amountPlaceholder": "es. 150.00",
    "qp.transferTitleLabel": "Oggetto del bonifico *",
    "qp.transferTitlePlaceholder": "es. Ordine adesivi #123",
    "qp.attachFiles": "Allega file (opzionale)",
    "qp.fileTypes": "PNG, JPG, PDF, SVG, TIFF, WEBP — max. 20 MB / file",
    "qp.uploading": "Caricamento...",
    "qp.clickToUpload": "Clicca per selezionare i file",
    "qp.orderNotes": "Note dell'ordine",
    "qp.notesPlaceholder": "Descrivi i dettagli dell'ordine, dimensioni, quantità, tipo di pellicola...",
    "qp.characters": "caratteri",
    "qp.verification": "Verifica",
    "qp.howMuch": "Quanto fa",
    "qp.enterResult": "Inserisci il risultato",
    "qp.correct": "Corretto!",
    "qp.incorrect": "Risultato errato",
    "qp.submit": "Effettua ordine",
    "qp.processing": "Elaborazione...",
    "qp.redirectInfo": "Dopo aver cliccato verrai reindirizzato al pagamento PayU.",
    "qp.successTitle": "Ordine ricevuto!",
    "qp.successText": "Il tuo ordine è stato ricevuto. Verrai reindirizzato al pagamento.",
    "qp.errorBot": "Attività sospetta rilevata.",
    "qp.errorTooFast": "Modulo compilato troppo rapidamente.",
    "qp.errorCaptcha": "Risposta di verifica errata.",
    "qp.errorAmount": "Inserisci un importo valido.",
    "qp.errorGeneral": "Si è verificato un errore durante la creazione del pagamento. Riprova.",
    "qp.orderPlaced": "Ordine effettuato!",

    "about.title": "Chi",
    "about.titleHighlight": "siamo",
    "about.p1": "è un marchio di proprietà di DRUKEXPERT.PL Sp. z o.o. con sede a Katowice, Polonia. Siamo specializzati nella stampa di adesivi della massima qualità su pellicole speciali – olografiche, glitter, specchio, trasparenti e molte altre.",
    "about.p2": "La nostra tipografia utilizza le tecnologie di stampa digitale più moderne, garantendo un'eccellente qualità dei colori e la durata degli adesivi.",
    "about.p3": "Offriamo adesivi sia kiss-cut che die-cut. Un'ampia selezione di pellicole e laminati ti permette di creare adesivi perfetti per ogni applicazione.",
    "about.stickersCut": "Stickers Cut è il nostro strumento online gratuito per generare il contorno di taglio degli adesivi – creato e gestito da Stickers Tattooo. Lo trovi su stickers-cut.com: carichi la grafica e lo strumento prepara una linea di taglio pronta in SVG o PDF, da usare subito nel nostro configuratore di adesivi.",
    "about.companyData": "Dati aziendali",
    "about.contact": "Contatto",
    "about.instagramChat": "Chat su Instagram",

    "cart.title": "Il tuo",
    "cart.titleHighlight": "Carrello",
    "cart.empty": "Il carrello è vuoto",
    "cart.goToCreator": "Creatore di adesivi",
    "cart.total": "Totale:",
    "cart.back": "Indietro",
    "cart.continueShopping": "Continua lo shopping",
    "cart.goToPayment": "Procedi al pagamento",
    "cart.clearCart": "Svuota carrello",
    "cart.cartCleared": "Carrello svuotato",
    "cart.discount_after_checkout": "Lo sconto di benvenuto verrà applicato dopo aver cliccato \"Procedi al pagamento\".",
    "cart.shippingMethod": "Metodo di spedizione",
    "cart.loading": "Caricamento...",
    "cart.products": "Prodotti",
    "cart.shipping": "Spedizione",
    "cart.gross": "lordo",
    "shipping.paczkomat_inpost": "Punto di ritiro InPost - consegna solo in Polonia",
    "shipping.kurier_inpost": "Corriere InPost - consegna solo in Polonia",
    "shipping.kurier_europa": "Corriere Europa DPD/DHL/FedEx",
    "shipping.paczkomat_europa": "Punto di ritiro InPost Europa",
    "shipping.odbior_osobisty": "Ritiro in sede",
    "shipping.uk_fedex": "Corriere UK e paesi extra UE",
    "shipping.kurier_irlandia": "Corriere Irlanda - FedEx / DPD",
    "shipping.kurier_hiszpania": "Corriere Spagna FedEx",
    "shipping.kurier_grecja": "Corriere Grecia",
    "shipping.locker_title": "Punto di ritiro",
    "shipping.locker_hint_pl": "Inserisci il numero del punto di ritiro InPost in Polonia (es. KRA010, WAW123M).",
    "shipping.locker_hint_eu": "Inserisci il numero del punto di ritiro InPost nel tuo paese europeo (es. AT01A, DE123456).",
    "shipping.locker_placeholder_pl": "Numero punto PL (es. KRA010)",
    "shipping.locker_placeholder_eu": "Numero punto Europa (es. AT01A)",
    "shipping.enter_locker": "Inserisci un numero di punto di ritiro!",

    "account.title": "Il mio account",
    "account.logout": "Esci",
    "account.tab.profile": "Profilo",
    "account.tab.orders": "Ordini",
    "account.tab.saved": "Salvati",
    "account.name": "Nome completo",
    "account.phone": "Telefono",
    "account.address": "Indirizzo di spedizione",
    "account.city": "Città",
    "account.zip": "Codice postale",
    "account.locker": "Numero punto di ritiro",
    "account.locker_hint": "Salva il tuo punto di ritiro preferito per velocizzare gli ordini",
    "account.save": "Salva",
    "account.saving": "Salvataggio...",
    "account.saved": "Dati salvati!",
    "account.save_error": "Errore di salvataggio",
    "account.loading": "Caricamento...",
    "account.no_orders": "Nessun ordine",
    "account.order": "Ordine",
    "account.date": "Data",
    "account.status": "Stato",
    "account.products": "Prodotti",
    "account.quantity": "Quantità",
    "account.dimensions": "Dimensioni",
    "account.foil": "Pellicola",
    "account.laminate": "Laminato",
    "account.total": "Totale",
    "account.download_pdf": "Scarica PDF",
    "account.product_n": "Prodotto",
    "account.no_configs": "Nessuna configurazione salvata",
    "account.no_configs_hint": "Salva una configurazione nel creatore di adesivi per vederla qui",
    "account.use": "Usa",
    "account.pcs": "pz",
    "account.delete_error": "Errore di eliminazione",
    "account.config_deleted": "Configurazione eliminata",
    "account.delete_account": "Elimina account e dati",
    "account.delete_title": "Eliminazione account",
    "account.delete_warning": "Questa azione è irreversibile. Verranno eliminati:",
    "account.delete_item1": "Il tuo profilo e i dati personali",
    "account.delete_item2": "Le configurazioni di adesivi salvate",
    "account.delete_item3": "L'account di accesso",
    "account.delete_orders_note": "La cronologia degli ordini verrà conservata per scopi contabili.",
    "account.cancel": "Annulla",
    "account.confirm_delete": "Sì, elimina l'account",
    "account.deleting": "Eliminazione...",
    "account.deleted": "Account eliminato",
    "account.delete_failed": "Impossibile eliminare l'account",
    "account.gdpr_note": "Secondo il GDPR hai il diritto di cancellare i tuoi dati personali",
    "account.status.new": "Nuovo",
    "account.status.unpaid": "Modifiche file / note",
    "account.status.processing": "In lavorazione",
    "account.status.shipped": "Spedito",
    "account.status.completed": "Completato",
    "account.status.cancelled": "Annullato",
    "account.pay_now": "Completa il pagamento",
    "account.delete_order": "Elimina ordine",

    "auth.login": "Accedi",
    "auth.register": "Crea account",
    "auth.reset": "Reimposta password",
    "auth.login_desc": "Accedi per gestire i tuoi ordini",
    "auth.register_desc": "Crea un account e approfitta degli sconti",
    "auth.reset_desc": "Ti invieremo un link per reimpostare la password",
    "auth.google": "Continua con Google",
    "auth.or_email": "o tramite email",
    "auth.name_placeholder": "Nome completo",
    "auth.password_placeholder": "Password (min. 6 caratteri)",
    "auth.loading": "Caricamento...",
    "auth.submit_login": "Accedi",
    "auth.submit_register": "Registrati",
    "auth.submit_reset": "Invia link",
    "auth.forgot": "Password dimenticata?",
    "auth.no_account": "Non hai un account?",
    "auth.has_account": "Hai già un account?",
    "auth.back_login": "← Torna all'accesso",
    "auth.reset_sent": "Il link per reimpostare la password è stato inviato alla tua email!",
    "auth.error": "Errore",
    "auth.register_error": "Errore di registrazione",
    "auth.login_error": "Errore di accesso",
    "auth.google_error": "Errore accesso Google",
    "auth.register_success": "Account creato! Controlla la tua email per confermare la registrazione.",
    "auth.login_success": "Accesso effettuato!",
    "auth.new_password_title": "Nuova password",
    "auth.new_password_placeholder": "Nuova password (min. 6 caratteri)",
    "auth.new_password_saving": "Salvataggio...",
    "auth.new_password_submit": "Cambia password",
    "auth.new_password_error": "Errore: ",
    "auth.new_password_success": "Password cambiata! Ora puoi accedere.",
    "auth.new_password_label": "Nuova password",
    "auth.confirm_password_label": "Conferma nuova password",
    "auth.confirm_password_placeholder": "Reinserisci la password",
    "auth.password_too_weak": "La password è troppo debole – deve avere almeno 6 caratteri",
    "auth.passwords_mismatch": "Le password non corrispondono",

    "checkout.title": "Ordine",
    "checkout.personalData": "Dati personali",
    "checkout.firstName": "Nome",
    "checkout.lastName": "Cognome",
    "checkout.email": "E-mail",
    "checkout.phone": "Telefono",
    "checkout.deliveryAddress": "Indirizzo di consegna",
    "checkout.street": "Via e numero",
    "checkout.zip": "Codice postale",
    "checkout.city": "Città",
    "checkout.wantInvoice": "Desidero una fattura IVA",
    "checkout.nip": "Partita IVA",
    "checkout.paymentMethod": "Metodo di pagamento",
    "checkout.products": "Prodotti:",
    "checkout.orderAndPay": "Ordina e paga",
    "checkout.emptyCart": "Il carrello è vuoto!",
    "checkout.orderSuccess": "Ordine effettuato! Reindirizzamento al pagamento...",
    "checkout.totalToPay": "Totale da pagare",
    "checkout.orderNotes": "Note dell'ordine",
    "checkout.orderNotesPlaceholder": "Inserisci note importanti, es. istruzioni speciali per il progetto, informazioni aggiuntive...",
    "checkout.characters": "caratteri",
    "checkout.creditCard": "Carta di credito",
    "checkout.saveToProfile": "Ricorda i dati per ordini futuri",
    "checkout.welcomeDiscount": "Sconto di benvenuto",
    "checkout.loyaltyUse": "Usa i punti fedeltà",
    "checkout.loyaltyPts": "pt",
    "checkout.loyaltyDiscount": "Sconto",
    "checkout.loyaltyMax50": "Max 50% del valore dell'ordine",
    "checkout.processing": "Elaborazione...",
    "checkout.companyDataTitle": "Dati aziendali dal registro GUS",
    "checkout.companyNameLabel": "Nome azienda",
    "checkout.loginRequired": "Devi essere connesso per effettuare un ordine",
    "checkout.sessionExpired": "La sessione è scaduta. Accedi di nuovo per completare l'ordine.",
    "cart.printOffer": "Salva offerta come PDF",

    "questions.text": "Domande sul funzionamento del creatore di adesivi?",
    "questions.link": "Scrivici",

    "tool.badge": "✂️ Strumento online",
    "tool.title": "Strumento contorno gratuito",
    "tool.description": "Crea un contorno vettoriale per il tuo adesivo — gratis!",

    "config.whiteHint": "Come preparare un file con sottostampa bianca?",
    "config.whiteHintLink": "Vedi le istruzioni nelle FAQ →",
    "config.whiteRecommend": "Per alcuni progetti consigliamo di attivare la sottostampa bianca — i colori saranno più vivaci e leggibili su sfondo trasparente.",

    "contact.error": "Invio del messaggio non riuscito. Riprova.",

    "shape.kiss-cut": "Adesivi kiss-cut (su fogli)",
    "shape.kiss-cut.desc": "Adesivi tagliati su foglio, facili da staccare",
    "shape.die-cut": "Adesivi die-cut",
    "shape.die-cut.desc": "Adesivi tagliati esattamente nella forma del design",
    "foil.biala": "Pellicola bianca",
    "foil.biala-mocny": "Pellicola bianca adesivo forte",
    "foil.przezroczysta": "Trasparente",
    "foil.przezroczysta-bialy": "Trasparente – con sottostampa bianca",
    "foil.ultra-przejrzysta": "Ultra trasparente / alta lucentezza",
    "foil.ultra-przejrzysta-bialy": "Ultra trasparente / alta lucentezza – con sottostampa bianca",
    "foil.holograficzna": "Olografica",
    "foil.holograficzna-bialy": "Olografica – con sottostampa bianca",
    "foil.holo-opalizowane": "Holo opalescente fosforescente",
    "foil.holo-opalizowane-bialy": "Holo opalescente fosforescente con sottostampa bianca",
    "foil.mini-brokat": "Mini glitter holo",
    "foil.mini-brokat-bialy": "Mini glitter holo – con sottostampa bianca",
    "foil.brokat-duzy": "Glitter grande holo",
    "foil.brokat-duzy-bialy": "Glitter grande holo – con sottostampa bianca",
    "foil.szklo-tluczone": "Vetro frantumato holo",
    "foil.szklo-tluczone-bialy": "Vetro frantumato holo – con sottostampa bianca",
    "foil.lustrzana": "Specchio argento (cromato)",
    "foil.lustrzana-bialy": "Specchio argento (cromato) – con sottostampa bianca",
    "foil.odblaskowe": "Pellicola riflettente / retroriflettente",
    "foil.szczotkowane-alu": "Alluminio spazzolato",
    "foil.szczotkowane-alu-bialy": "Alluminio spazzolato – con sottostampa bianca",
    "foil.plotno-canvas": "Adesivi canvas",
    "foil.magnetyczne": "Adesivi magnetici",
    "foil.zielonkawa-glow": "Pellicola verde fosforescente",
    "foil.zielonkawa-glow-bialy": "Pellicola verde fosforescente – con sottostampa bianca",
    "lam.polysk": "Laminato lucido",
    "lam.ultra-polysk": "Laminato ultra trasparente / alta lucentezza",
    "lam.ultra-polysk-brokat": "Laminato ultra trasparente / alta lucentezza con Glitter",
    "lam.mat": "Laminato opaco",
    "lam.brokat-mat": "Laminato glitter opaco",
    "lam.krzyzyk-plotno": "Laminato trama incrociata canvas",
    "lam.holo": "Laminato holo",
    "lam.holo-brokat": "Laminato holo glitter",
    "lam.holo-gwiazdki": "Laminato holo stelle",
    "lam.holo-tluczone": "Laminato holo vetro frantumato",
    "lam.holo-piksele": "Laminato holo pixel",
    "lam.skora": "Laminato texture pelle",
    "lam.podlogowy": "Laminato per pavimenti / opaco",
    "lam.holo-piasek-mini": "Laminato holo sabbia con mini stelle",
    "lam.holo-duze-gwiazdki": "Laminato holo grandi stelle",
    "lam.holo-duze-piksele": "Laminato holo grandi pixel",
    "lam.nielam-polysk": "Stampa vinile alta brillantezza – adesivi non laminati",
    "lam.nielam-mat": "Stampa vinile opaco – adesivi non laminati",
    "lam.bez-lam": "Senza laminazione",
    "lam.ultra-polysk-sztywny": "Laminato ultra trasparente / lucido intenso – con pellicola protettiva, laminato rigido",
    "lam.ultra-polysk-brokat-sztywny": "Laminato ultra trasparente / lucido intenso con Glitter – con pellicola protettiva, laminato rigido",
    "lam.flatNote": "per superfici piane",

    "faq.q1": "Non sono sicuro che il mio file sia adatto alla stampa?",
    "faq.a1": "Inviaci il tuo file e il nostro team lo verificherà gratuitamente e ti informerà delle eventuali correzioni necessarie. Accettiamo file PDF, AI, EPS, SVG e PNG/JPG ad alta risoluzione (min. 300 DPI; consigliamo 600 DPI).",
    "faq.q2": "Adesivi su pellicola trasparente – senza e con sottostampa bianca?",
    "faq.a2": "Gli adesivi senza sottostampa bianca saranno semi-trasparenti – i colori saranno visibili ma lo sfondo trasparirà. Gli adesivi con sottostampa bianca hanno uno strato di inchiostro bianco sotto i colori che migliora la leggibilità dei colori, ma nella stampa solvente rimane semi-trasparente, quindi su pellicola trasparente lo sfondo può trasparire leggermente.",
    "faq.q3": "Come preparare un file per la stampa?",
    "faq.a3": "Il file deve essere in formato vettoriale (PDF, AI, EPS, SVG) o raster a min. 300 DPI (consigliamo 600 DPI). Aggiungi 2mm di abbondanza. Colori in CMYK. La linea di taglio deve essere su un livello separato.",
    "faq.q4": "Conferma dei file prima della stampa",
    "faq.a4": "Prima della stampa inviamo un'anteprima per l'approvazione. La stampa inizia solo dopo la tua conferma.",
    "faq.q5": "Come effettuare un ordine?",
    "faq.a5": "Configura i tuoi adesivi nel creatore – scegli forma, tipo di pellicola, laminato, dimensioni e quantità. Carica il tuo file di progetto, aggiungi al carrello e procedi al checkout. Dopo il pagamento, prepareremo i tuoi adesivi.",
    "faq.q6": "Qual è la differenza tra adesivi die-cut e kiss-cut?",
    "faq.a6": "Die-cut – adesivo tagliato esattamente nella forma del design, senza sfondo extra. Kiss-cut – adesivo tagliato su un foglio, facile da staccare, con sfondo rettangolare intorno al design.",
    "faq.q7": "Offrite adesivi trasparenti, olografici o metallizzati con sottostampa bianca?",
    "faq.a7": "Sì! Offriamo un'ampia selezione di pellicole speciali con opzione di sottostampa bianca: trasparenti, olografiche, glitter, specchio (cromato), alluminio spazzolato e molte altre. La sottostampa bianca migliora la visibilità dei colori, ma nella stampa solvente non garantisce una copertura totale – su pellicole trasparenti lo sfondo può trasparire.",
    "faq.q8": "Come preparare la sottostampa bianca?",
    "faq.a8": "La sottostampa bianca deve essere preparata come un livello separato nel file. Il livello deve essere chiamato 'White' o 'Sottostampa bianca'. Contrassegna tutti gli elementi con il colore 100% Spot White.",
    "faq.q9": "Il mio ordine non è stato stampato correttamente, cosa devo fare?",
    "faq.a9": "Contattaci immediatamente inviando foto degli adesivi difettosi alla nostra email. Esamineremo il reclamo e proporremo una soluzione.",
    "faq.q10": "Quanto tempo richiederà l'elaborazione di un reclamo?",
    "faq.a10": "Elaboriamo i reclami entro 3–5 giorni lavorativi dalla ricezione della segnalazione con foto.",
    "faq.q11": "Quando riceverò il mio ordine?",
    "faq.a11": "Il tempo di produzione standard è di 3–5 giorni lavorativi dall'approvazione del file e dal pagamento. La spedizione tramite DPD o InPost richiede altri 1–2 giorni lavorativi.",
    "faq.q12": "Quali sono i dati aziendali e le informazioni di pagamento?",
    "faq.a12": "STICKERS TATTOOO / DRUKEXPERT.PL Sp. z o.o., ul. Głogowa 39, 40-750 Katowice. Partita IVA e altri dettagli disponibili sulla fattura.",
    "faq.q13": "Emettete fatture IVA?",
    "faq.a13": "Sì, emettiamo fatture IVA. Seleziona l'opzione 'Fattura IVA' al momento dell'ordine e fornisci i tuoi dati di fatturazione.",
    "faq.q14": "Ordine calcolato erroneamente che richiede un pagamento aggiuntivo.",
    "faq.a14": "Se dopo la verifica del file le dimensioni degli adesivi differiscono da quelle dell'ordine, ti contatteremo per eventuali pagamenti aggiuntivi o correzioni.",

    "calc.badge": "Calcolatore",
    "calc.title": "Verifica",
    "calc.titleHighlight": "il prezzo",
    "calc.subtitle": "Sposta i cursori e vedi il prezzo degli adesivi in tempo reale",
    "calc.discount": "Sconto quantità",
    "calc.unitPrice": "Prezzo unitario",
    "calc.totalPrice": "Prezzo totale",
    "calc.note": "Prezzo indicativo per Pellicola Bianca die-cut con laminato. Prezzo esatto nel configuratore.",
    "calc.noteGeneric": "Prezzo indicativo per la pellicola e il laminato selezionati. Prezzo esatto nel configuratore.",
    "calc.pcs": "pz",

    "insp.badge": "Portfolio",
    "insp.title": "Le nostre",
    "insp.titleHighlight": "creazioni",
    "insp.subtitle": "Scopri come appaiono i nostri adesivi in vari settori",
    "insp.industry.all": "Tutti",
    "insp.industry.gastronomia": "Gastronomia",
    "insp.industry.kosmetyki": "Cosmetica",
    "insp.industry.IT": "IT / Tech",
    "insp.industry.moda": "Moda",
    "insp.industry.motoryzacja": "Automotive",
    "insp.industry.inne": "Altro",

    "review.badge": "Recensioni",
    "review.title": "Cosa dicono",
    "review.titleHighlight": "i nostri clienti",
    "review.reviewsCount": "recensioni",
    "review.noReviews": "Sii il primo a lasciare una recensione!",
    "review.addReview": "Aggiungi recensione",
    "review.addReviewTitle": "Aggiungi la tua recensione",
    "review.yourName": "Il tuo nome",
    "review.namePlaceholder": "Marco R.",
    "review.yourRating": "Valutazione",
    "review.yourReview": "La tua recensione",
    "review.reviewPlaceholder": "Condividi la tua esperienza con i nostri adesivi...",
    "review.send": "Invia recensione",
    "review.sending": "Invio...",
    "review.fillAll": "Compila il nome e la recensione",
    "review.loginRequired": "Accedi per aggiungere una recensione",
    "review.submitted": "Grazie! La tua recensione verrà pubblicata dopo la verifica.",
    "review.alreadySubmitted": "Puoi inviare solo una recensione.",
    "review.moderationNote": "La tua recensione verrà pubblicata dopo la verifica del nostro team",

    "loyalty.title": "Programma fedeltà",
    "loyalty.subtitle": "Accumula punti con gli ordini",
    "loyalty.points": "punti",
    "loyalty.worth": "Valore sconto:",
    "loyalty.minRedeem": "Minimo per riscattare",
    "loyalty.earnInfo": "Per ogni {rate} PLN spesi guadagni 1 punto. Riscatta i punti per uno sconto sul prossimo ordine!",
    "loyalty.history": "Cronologia punti",
    "loyalty.howItWorks": "Come funziona?",
    "loyalty.rule1": "Per ogni {rate} PLN spesi ricevi 1 punto.",
    "loyalty.rule2": "Ogni punto vale {value} PLN di sconto.",
    "loyalty.rule3": "Puoi riscattare i punti a partire da {min} pt minimo.",
    "loyalty.example": "📌 Esempio:",
    "loyalty.exampleCalc": "Ordine da {amount} PLN → ottieni {pts} pt → sconto: {value} PLN. Quindi 100 pt = 5 PLN di sconto!",
    "loyalty.reviewBonus": "Aggiungi una recensione e guadagna 100 punti (= 5 PLN di sconto)!",
    "account.tab.loyalty": "Punti",

    "ui.changeTheme": "Cambia tema",
    "ui.hideGhost": "Nascondi fantasma",
    "ui.unlockGhost": "Sblocca fantasma",
    "ui.lockGhost": "Blocca fantasma nell'angolo",
    "config.qtyPlaceholder": "es. 250",
    "config.customOrderNote": "Per ordini molto grandi o non standard prepariamo preventivi individuali. Contattaci via email",
    "config.customOrderNoteOr": "o",
    "config.minReached": "Ordine minimo raggiunto",
    "config.canOrder": "puoi ordinare!",
    "config.minNeeded": "mancano",

    "seo.faq.title": "FAQ – Domande frequenti | Tipografia Adesivi Stickers Tattooo",
    "seo.faq.description": "Risposte alle domande frequenti sulla stampa di adesivi: pellicole, laminati, formati file, tempi di produzione, spedizione. Stickers Tattooo.",
    "seo.blog.title": "Blog stampa adesivi – Guide | Stickers Tattooo",
    "seo.blog.description": "Blog della tipografia Stickers Tattooo. Guide: preparazione file, tipi di pellicole, laminati, adesivi olografici e altro.",
    "seo.contact.title": "Contatto – Tipografia Stickers Tattooo | Katowice",
    "seo.contact.description": "Contatta la tipografia Stickers Tattooo. Tel: +48 534 900 620, email: stickers.tattooo@wp.pl. Katowice, ul. Głogowa 39.",
    "seo.about.title": "Chi siamo – Tipografia Adesivi Stickers Tattooo | Katowice",
    "seo.about.description": "Scopri Stickers Tattooo – tipografia di adesivi premium da Katowice. Adesivi in vinile, olografici e die-cut dal 2020.",
    "seo.foils.title": "Tipi di pellicole – 15 pellicole | Stickers Tattooo",
    "seo.foils.description": "Confronto di 15 tipi di pellicole: bianca, trasparente, olografica, glitter, specchio cromato, riflettente, magnetica, fosforescente.",
    "seo.breadcrumb.home": "Home",
  },
};

// Configurator-specific dictionaries live in their own files to keep this one readable.
(Object.keys(translations) as Lang[]).forEach((l) => {
  Object.assign(translations[l], sheetsTranslations[l], configuratorTranslations[l]);
});



interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextType>({
  lang: "pl",
  setLang: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    const saved = localStorage.getItem("lang") as Lang;
    return saved && translations[saved] ? saved : "pl";
  });

  const changeLang = (newLang: Lang) => {
    setLang(newLang);
    localStorage.setItem("lang", newLang);
    document.documentElement.lang = newLang;
  };

  // Set initial lang attribute on <html>
  if (typeof document !== "undefined") {
    document.documentElement.lang = lang;
  }

  const t = (key: string): string => {
    return translations[lang]?.[key] || translations["pl"]?.[key] || key;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang: changeLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useLocalePath() {
  const { lang } = useI18n();
  // Always emit the canonical slug for the active language (no redirect hop).
  return useCallback((path: string) => canonicalPathFor(`/${lang}${path}`, lang) || `/${lang}${path}`, [lang]);
}
